import useAPI from "@toothfairy/shared-api/useApi";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { Popable } from "react-native-popable";
import { isConnectedToCloud } from "../../../../App";
import envConfig from "../../../../envConfig";
import Files from "../../../API/Files";
import Live from "../../../API/Live";
import Media from "../../../API/Media";
import trainingAPI from "../../../API/Training/trainingAPI";
import AudioRecorder from "../../../Components/AudioRecorder";
import VoiceWave from "./VoiceWave";
import { useLiveVoice } from "./LiveVoiceContext";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import { generateRowId } from "../../../Utility";
import {
  buildAgentMentionRegex,
  buildClipboardFile,
  captureTokenFromNameOrUrl,
  clipboardCaptureLabel,
  detectClipboardSource,
  ENABLE_CHIP_COMPOSER,
  extractClipboardParts,
  findAgentMention,
  insertTokenAtCursor,
  normalizeCaptureFileName,
  shouldCaptureAsFile,
  tokenizeForDisplay,
} from "../../../Utility/clipboardUtils";
import { excelHtmlToXlsxFile } from "../../../Utility/clipboardFileConversion";
import { convertImageToPng } from "../../../Utility/imageUtils";
import TokenComposerInput from "../../../Components/TokenComposerInput";
import ChatMediaLeaf from "./ChatMediaLeaf";
import StructuredQuestionsInput from "./StructuredQuestionsInput";
import { _tools } from "../../Settings/Components/QA/SubComponents/QaSettingsAIView";
import { isLiveModeAvailableInRegion } from "../../../Utils/regionWebUrl";
import { isMobileScreen } from "../../MainViewContainer";
const controller = new AbortController();
// Stable empty array to prevent re-renders when availableFunctions prop is not provided
const EMPTY_FUNCTIONS = [];

const ChatWebInput = ({
  placeholder = null,
  isSpeechGettingProcessed = false,
  stopAudio = () => {},
  onSend = () => {},
  isStreaming,
  onCancel = () => {},
  allowStop = false,
  isLastMessageEmpty,
  showWidgetChatsHistory,
  canInteractWithChat,
  isAwaitingUserInput = false,
  messages = [],
  quickQuestions = [],
  isChatEmpty = false,
  primaryAgentId,
  agents = [],
  onAgentSelected = () => {},
  onSendWithAgent = null,
  agentMode = "all",
  agenticRAG = false,
  useInterleavedReasoning = false,
  text = "",
  setText = () => {},
  agentDesktopVM = null,
  allowImagesUpload = false,
  allowAudiosUpload = false,
  allowVideosUpload = false,
  // Voice-over capability (allowAudiosGeneration) — also gates the live
  // voice mode entry point: live voice is a voice feature.
  allowAudiosGeneration = false,
  allowFileUpload = false,
  allowCodeUpload = false,
  onImageUploaded = () => {},
  onAudioUploaded = () => {},
  onVideoUploaded = () => {},
  onFileUploaded = () => {},
  onFilesUploaded = () => {},
  videoUrl,
  setVideoUrl,
  imageUrl,
  setImageUrl,
  audioUrl,
  setAudioUrl,
  fileUrl,
  setFileUrl,
  fileUrls,
  setFileUrls,
  imageUrls,
  audioUrls,
  isAutoReplyMode = false,
  isPlaying = false,
  allowMiscFileTypes = false,
  isPlanner = false,
  isPlannerAcceptingFeedback = false,
  isAgenticRAGAcceptingFeedback = false,
  forcePlannerStop = false,
  planningExecution,
  handedOffToHuman = false,
  voiceSensitivity = "medium",
  documentId = null,
  handleCanvasCreation = () => {},
  isCanvasOpen = true,
  onToggleCanvas = () => {},
  agenticTooling = false,
  // LiveKit Voice Mode props
  workspaceId = null,
  apiKey = null,
  chatId = null,
  // Live session (mixed text/voice) props
  liveSessionId = null,
  onVoiceSessionEnded = () => {},
  // Plan Mode props
  planMode = false,
  planFile = null,
  onPlanModeToggle = () => {},
  onShowPlan = () => {},
  planStatus = null,
  forcePlanMode = false,
  // Agent capabilities for tool filtering
  agentCharting = false,
  agentAllowInternetSearch = false,
  agentAllowDeepInternetSearch = false,
  agentAllowImagesGeneration = false,
  agentAllowVideosGeneration = false,
  agentHasCode = false,
  agentHasImages = false,
  agentEmailAsSender = false,
  agentPhoneNumberAsSender = false,
  agentWhatsappNumberAsSender = false,
  agentAllowAskQuestion = true,
  // Custom functions
  availableFunctions = EMPTY_FUNCTIONS,
  // Folder binding props
  allowFolderBinding = false,
  onFolderBound = () => {},
  onFolderUnbound = () => {},
  boundFolder = null,
  recentFolders = [],
  onUpdateRecentFolders = () => {},
  // Home mode
  isHomeMode = false,
}) => {
  const { t } = useTranslation();
  const source = axios.CancelToken.source();
  const AppToasty = AppToast.useToast();
  const { currentTheme } = AppTheme.useTheme();
  const textareaRef = useRef();
  // Counts clipboard-pasted images so each gets a unique name (image 1, image 2, ...)
  const pastedImageCounterRef = useRef(0);
  // Counts clipboard captures of rich/large text (<clipboard 1>, <clipboard 2>, ...)
  const pastedClipboardCounterRef = useRef(0);
  const quickQuestionsContainerRef = useRef(null);
  const [quickQuestionsWidth, setQuickQuestionsWidth] = useState(420);
  const [isTyping, setIsTyping] = useState(false);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(true);
  const [prevScrollTop, setPrevScrollTop] = useState(0);
  const [fileUploadInProgress, setFileUploadInProgress] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [filesUploadProgress, setFilesUploadProgress] = useState([]);
  const [fileUploadStatus, setFileUploadStatus] = useState(""); // "converting", "uploading", ""
  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const [recordingUploadInProgress, setRecordingUploadInProgress] = useState(
    false,
  );
  const [recordingInProgress, setRecordingInProgress] = useState(false);
  const [recordingUploadProgress, setRecordingUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [fileExtension, setFileExtension] = useState("");
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionStartPosition, setMentionStartPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showToolDropdown, setShowToolDropdown] = useState(false);
  const [toolQuery, setToolQuery] = useState("");
  const [filteredTools, setFilteredTools] = useState([]);
  const [toolStartPosition, setToolStartPosition] = useState(0);
  // Tool selection is text-driven: `~tool_id~` tokens in the input ARE the
  // selection (rendered as inline removable pills by the chip composer).
  // Derived from the current text so pills, dropdown guard and folder
  // binding never disagree.
  const selectedToolIds = useMemo(
    () =>
      tokenizeForDisplay(text || "")
        .filter((segment) => segment.kind === "tool")
        .map((segment) => segment.value.replace(/~/g, "")),
    [text],
  );
  // Friendly labels for tool pills (e.g. "~write_file~" -> "File Writing").
  // _tools[].name is an i18n key (settings.qa.*) - translate it here; fall
  // back to the id when a key has no translation.
  const chipLabelMap = useMemo(() => {
    const map = {};
    _tools.forEach((tool) => {
      if (!tool?.id) return;
      const translated = t(tool.name);
      map[`~${tool.id}~`] =
        translated && translated !== tool.name ? translated : tool.id;
    });
    (availableFunctions || EMPTY_FUNCTIONS).forEach((fn) => {
      if (fn?.name) map[`~${fn.name}~`] = fn.name;
    });
    return map;
  }, [availableFunctions, t]);
  // Friendly labels for clipboard capture pills, e.g. "<clipboard 1>" ->
  // "Excel capture 1" (set at paste time from the detected source app)
  const [clipboardLabels, setClipboardLabels] = useState({});
  const composerLabelMap = useMemo(
    () => ({ ...chipLabelMap, ...clipboardLabels }),
    [chipLabelMap, clipboardLabels],
  );
  // Capture tokens currently inlined in the text (their attachment chips are
  // hidden to avoid duplication; deleting the pill makes the chip reappear)
  const captureTokensInText = useMemo(
    () =>
      new Set(
        tokenizeForDisplay(text || "")
          .filter((segment) => segment.kind !== "text")
          .map((segment) => segment.value),
      ),
    [text],
  );
  const isCaptureChipVisible = (nameOrUrl) => {
    const token = captureTokenFromNameOrUrl(nameOrUrl);
    return !token || !captureTokensInText.has(token);
  };
  // Deleting a capture pill (backspace or inline ✕) removes the attachment
  // itself - reconciliation keeps files and tokens 1:1. Only files we
  // captured from the clipboard are swept (never regular user uploads). The
  // send guard prevents stripping attachments while a message is being sent
  // (send clears the text first).
  const capturedFileNamesRef = useRef(new Set());
  const sendingRef = useRef(false);
  useEffect(() => {
    if (sendingRef.current) return;
    const sweep = (urls, kind) => {
      // Collect first, remove in descending index order: each removal
      // splices the parent's array, so ascending indices would go stale
      const removals = [];
      (urls || []).forEach((url, index) => {
        const base = normalizeCaptureFileName(
          decodeURIComponent((url || "").split("?")[0].split("/").pop() || ""),
        );
        if (!capturedFileNamesRef.current.has(base)) return;
        const token = captureTokenFromNameOrUrl(url);
        if (token && !captureTokensInText.has(token)) {
          capturedFileNamesRef.current.delete(base);
          removals.push(index);
        }
      });
      removals
        .sort((a, b) => b - a)
        .forEach((index) => onFilesUploaded(null, null, index, kind));
    };
    sweep(fileUrls, "file");
    sweep(imageUrls, "image");
  }, [captureTokensInText, fileUrls, imageUrls]);
  const markSending = () => {
    sendingRef.current = true;
    setTimeout(() => {
      sendingRef.current = false;
    }, 0);
  };
  // Agent mention state (# trigger)
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [agentQuery, setAgentQuery] = useState("");
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [agentStartPosition, setAgentStartPosition] = useState(0);
  // Labels known to the mention matcher (used by # selection and the pill)
  const agentLabels = useMemo(
    () =>
      (agents || [])
        .map((agent) => agent?.label || agent?.name)
        .filter(Boolean),
    [agents],
  );
  // Agent delegation is text-driven: the "@Agent Label" mention pill in the
  // input IS the selection (inserted via #, removed via the pill). In home
  // mode mentions stay plain text for multi-agent routing downstream; in
  // normal mode the first mention picks the agent (tool filtering, send
  // routing) and is stripped from the sent text.
  const selectedAgent = useMemo(() => {
    if (isHomeMode) return null;
    const mention = findAgentMention(text || "", agentLabels);
    if (!mention) return null;
    return (
      (agents || []).find(
        (agent) => (agent?.label || agent?.name || agent?.id) === mention.label,
      ) || null
    );
  }, [text, agentLabels, agents, isHomeMode]);
  // Splits the outgoing text into (clean text, delegated agent) for sending:
  // the mention pill rides the draft, not the message.
  const deriveAgentRouting = (value) => {
    if (isHomeMode) return { text: value, agent: null };
    const mention = findAgentMention(value, agentLabels);
    if (!mention) return { text: value, agent: null };
    const agent =
      (agents || []).find(
        (a) => (a?.label || a?.name || a?.id) === mention.label,
      ) || null;
    if (!agent) return { text: value, agent: null };
    const afterMention = mention.index + mention.mention.length;
    const eatTrailingSpace = value[afterMention] === " " ? 1 : 0;
    const stripped =
      value.slice(0, mention.index) +
      value.slice(afterMention + eatTrailingSpace);
    return { text: stripped, agent };
  };
  // Tips/hints rotation state
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [showTip, setShowTip] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [displayedTipText, setDisplayedTipText] = useState("");
  // Expanded input state
  const [isExpanded, setIsExpanded] = useState(false);
  // Placeholder typing animation state
  const [placeholderText, setPlaceholderText] = useState("");
  const [inputReady, setInputReady] = useState(false);

  // Resolve the actual placeholder string from translation key or prop
  const resolvedPlaceholder = placeholder || t("chats.inputPlaceholder");

  // Extract structured questions from last message metadata
  const structuredQuestions = useMemo(() => {
    if (!messages || messages.length === 0) return null;

    try {
      // Get the last message
      const lastMessage = messages[messages?.length - 1];

      // Get metadata
      let metadata = lastMessage?.metadata;
      if (!metadata) return null;

      // Parse if string
      if (typeof metadata === "string") {
        try {
          metadata = JSON.parse(metadata);
        } catch (e) {
          return null;
        }
      }

      // Return structured_questions if exists
      return metadata?.structured_questions || null;
    } catch (e) {
      console.error(
        "Failed to extract structured_questions from last message:",
        e,
      );
      return null;
    }
  }, [JSON.stringify(messages)]);

  // Placeholder typing animation effect
  useEffect(() => {
    let currentText = "";
    const words = resolvedPlaceholder?.split(" ");
    if (words?.length > 0) {
      setTimeout(() => {
        const intervalId = setInterval(() => {
          if (words.length > 0) {
            currentText += words.shift() + " ";
            setPlaceholderText(currentText);
          } else {
            clearInterval(intervalId);
            setInputReady(true);
            setTimeout(() => {
              textareaRef.current?.focus();
            }, 100);
          }
        }, 30);
      }, 20);
    }
  }, [placeholder]);

  // Folder binding state
  const [boundFolderPath, setBoundFolderPath] = useState(
    boundFolder?.path || null,
  );
  const [boundFolderName, setBoundFolderName] = useState(
    boundFolder?.name || null,
  );
  const [isFolderSelectionOpen, setIsFolderSelectionOpen] = useState(false);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);

  // LiveKit Voice Agent state lives in LiveVoiceContext (hoisted above
  // the splash→chat view swap) so the session survives the remount that
  // happens when the first message creates the chat.
  const {
    isLiveKitMode,
    isLiveKitConnected,
    liveKitConnectionState,
    liveKitAgentState,
    isLiveKitMicEnabled,
    liveKitUserTranscript,
    liveKitAgentTranscript,
    liveKitError,
    liveKitRoomName,
    liveKitAudioLevel,
    liveKitAudioSpectrum,
    isLiveKitAudioDetected,
    sendLiveKitData,
    toggleLiveKitMicrophone,
    LIVEKIT_AGENT_STATES,
    LIVEKIT_CONNECTION_STATES,
    showLiveKitConfirmation,
    setShowLiveKitConfirmation,
    availableAudioDevices,
    selectedAudioInputDevice,
    setSelectedAudioInputDevice,
    selectedAudioOutputDevice,
    setSelectedAudioOutputDevice,
    isLoadingDevices,
    activeAudioDevices,
    liveKitLogs,
    clearLiveKitLogs,
    showDebugPanel,
    setShowDebugPanel,
    openLiveKitConfirmation,
    startLiveKitMode,
    stopLiveKitMode,
  } = useLiveVoice();

  // Close folder dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFolderDropdown) {
        const target = event.target;
        const dropdownElement = target?.closest?.(
          '[data-folder-dropdown="true"]',
        );
        if (!dropdownElement) {
          setShowFolderDropdown(false);
        }
      }
    };
    if (showFolderDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFolderDropdown]);

  useEffect(() => {
    const updateWidth = () => {
      if (quickQuestionsContainerRef.current) {
        const containerWidth = quickQuestionsContainerRef.current.offsetWidth;
        const textMaxWidth = containerWidth - 20 - 32 - 3;
        setQuickQuestionsWidth(textMaxWidth > 0 ? textMaxWidth : 420);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [displayQuickQuestions]);

  const displayQuickQuestions =
    !(showWidgetChatsHistory || canInteractWithChat === false) &&
    isChatEmpty &&
    quickQuestions &&
    quickQuestions?.length > 0;
  const {
    loading: audioProcessingInProgress,
    apiRequest: audioProcessingRequest,
    wasInvoked: audioProcessingInvoked,
    data: audioProcessingResult,
  } = useAPI(Media.processAudio, null, () => {});

  // Build accepted file types string for the unified file input
  const acceptedFileTypes = useMemo(() => {
    const types = [];
    if (allowImagesUpload) {
      types.push(
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/bmp",
        "image/webp",
      );
    }
    if (allowAudiosUpload) {
      types.push("audio/wav", "audio/mp4");
    }
    if (allowVideosUpload) {
      types.push("video/mp4");
    }
    if (allowFileUpload) {
      types.push(
        ".pptx",
        ".ppt",
        ".json",
        ".pdf",
        ".csv",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".html",
        ".txt",
        ".md",
      );
      if (allowCodeUpload) {
        types.push(
          ".py",
          ".js",
          ".java",
          ".cpp",
          ".c",
          ".cs",
          ".php",
          ".rb",
          ".swift",
          ".go",
          ".ts",
          ".rs",
          ".kt",
          ".scala",
          ".m",
          ".r",
          ".pl",
          ".sh",
          ".sql",
          ".vb",
          ".lua",
          ".dart",
          ".groovy",
          ".jl",
          ".hs",
          ".f90",
          ".pas",
          ".elm",
          ".clj",
        );
      }
      if (allowMiscFileTypes) {
        types.push(".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp");
      }
    }
    return types.join(", ");
  }, [
    allowImagesUpload,
    allowAudiosUpload,
    allowVideosUpload,
    allowFileUpload,
    allowCodeUpload,
    allowMiscFileTypes,
  ]);

  // Single unified upload option
  const showUploadOption = useMemo(() => {
    return (
      !showWidgetChatsHistory &&
      (allowImagesUpload ||
        allowAudiosUpload ||
        allowVideosUpload ||
        allowFileUpload)
    );
  }, [
    showWidgetChatsHistory,
    allowImagesUpload,
    allowAudiosUpload,
    allowVideosUpload,
    allowFileUpload,
  ]);

  const {
    generatorConfigData,
    handleGenerationOfCustomPrompt,
    isUserContentAdmin,
    getActiveWorkspace,
  } = useWorkspaces();

  // Build available tips based on current features
  const availableTips = useMemo(() => {
    const tips = [
      {
        key: "newline",
        text: t("chats.placeholders.shiftEnterNewLine"),
        icon: "↵",
      },
    ];

    // Add prompt tip if prompts are available
    if (generatorConfigData?.length > 0) {
      tips.push({
        key: "prompts",
        text: t("chats.placeholders.typeAtForPrompts"),
        icon: "",
      });
    }

    // Add tools tip if tools are available
    if (agentMode !== "voice") {
      tips.push({
        key: "tools",
        text: t("chats.placeholders.typeTildeForTools"),
        icon: "",
      });
    }

    // Add agent switching tip if agents are available and mode allows it
    if (
      agents?.length > 1 &&
      agentMode !== "planner" &&
      agentMode !== "voice"
    ) {
      tips.push({
        key: "agents",
        text: isHomeMode
          ? t("chats.placeholders.typeHashForDelegation")
          : t("chats.placeholders.typeHashForAgents"),
        icon: "",
      });
    }

    // Add plan mode toggle tip
    if ((agenticRAG || useInterleavedReasoning) && !forcePlanMode) {
      tips.push({
        key: "planmode",
        text: t("chats.placeholders.shiftTabPlanMode"),
        icon: "⇧",
      });
    }

    // Add file upload tips
    if (showUploadOption) {
      tips.push({
        key: "dragdrop",
        text: t("chats.placeholders.dragDropFiles"),
        icon: "📎",
      });
      tips.push({
        key: "paste",
        text: t("chats.placeholders.pasteImages"),
        icon: "📋",
      });
    }

    return tips;
  }, [
    t,
    generatorConfigData,
    agentMode,
    agents,
    agenticRAG,
    useInterleavedReasoning,
    forcePlanMode,
    showUploadOption,
  ]);

  // Select a random tip when chat session changes
  useEffect(() => {
    if (availableTips.length === 0 || agentMode === "voice" || isChatEmpty)
      return;

    // Select random tip for this session
    const randomIndex = Math.floor(Math.random() * availableTips.length);
    setCurrentTipIndex(randomIndex);
    setShowTip(!isChatEmpty);
    setHasStartedTyping(false);
  }, [chatId, availableTips.length, agentMode, isChatEmpty]);

  // Get current tip
  const currentTip = availableTips[currentTipIndex % availableTips.length];

  // Typing effect for tips (like someone typing)
  useEffect(() => {
    if (!showTip || !currentTip?.text || isChatEmpty) {
      setDisplayedTipText("");
      return;
    }

    setDisplayedTipText("");
    let charIndex = 0;
    const fullText = currentTip.text;

    const typingInterval = setInterval(() => {
      if (charIndex < fullText.length) {
        setDisplayedTipText(fullText.substring(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 15);

    return () => clearInterval(typingInterval);
  }, [currentTipIndex, showTip, currentTip?.text, isChatEmpty]);

  // Rotate tips until user starts typing
  useEffect(() => {
    if (
      !showTip ||
      hasStartedTyping ||
      availableTips.length <= 1 ||
      isChatEmpty
    )
      return;

    const rotationInterval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % availableTips.length);
    }, 4000);

    return () => clearInterval(rotationInterval);
  }, [showTip, hasStartedTyping, availableTips.length, isChatEmpty]);
  const fileSizeConstant = 20;
  const {
    data: S3uploadUrlData,
    loading: urlUploadGenerationInProgress,
    apiRequest: S3uploadUrlRequest,
    response: S3uploadResponse,
  } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const onRecordingUploaded = async (url) => {
    const result = await audioProcessingRequest({
      token: getActiveWorkspace()?.workspaceToken,
      data: {
        audio_file: url,
        workspaceid: getActiveWorkspace()?.id,
        chat_mode: true,
      },
    });
    if (result?.contents != undefined) {
      if (isAutoReplyMode) {
        onSend(result?.contents);
      } else {
        textareaRef.current.value += `${result?.contents}`;
      }
    }
  };
  const handleRecordingStarted = () => {
    setRecordingInProgress(true);
  };

  const detectExtensionFromFileName = (fileUurl) => {
    if (!fileUurl) return "";
    try {
      const url = new URL(fileUurl);
      const pathname = url.pathname;
      const lastSlashIndex = pathname.lastIndexOf("/");
      if (lastSlashIndex !== -1) {
        const filename = pathname.substring(lastSlashIndex + 1);
        const dotIndex = filename.lastIndexOf(".");
        if (dotIndex !== -1) {
          return filename.substring(dotIndex + 1);
        }
      }
    } catch (e) {
      // If URL parsing fails, try to extract extension from the string directly
      const lastSlashIndex = fileUurl.lastIndexOf("/");
      const filename =
        lastSlashIndex !== -1
          ? fileUurl.substring(lastSlashIndex + 1)
          : fileUurl;
      const dotIndex = filename.lastIndexOf(".");
      if (dotIndex !== -1) {
        return filename.substring(dotIndex + 1);
      }
    }
    return "";
  };

  const detectFileName = (fileUurl) => {
    if (!fileUurl) return "file";
    try {
      const url = new URL(fileUurl);
      const pathname = url.pathname;
      const lastSlashIndex = pathname.lastIndexOf("/");
      if (lastSlashIndex !== -1) {
        let filename = pathname.substring(lastSlashIndex + 1);
        const dotIndex = filename.lastIndexOf(".");
        if (dotIndex !== -1) {
          filename = filename.substring(0, dotIndex);
        }
        // Remove timestamp prefix (13 digits) that was prepended for uniqueness
        // Format is: 1732801887278filename -> filename
        if (/^\d{13}/.test(filename)) {
          filename = filename.substring(13);
        }
        return filename || "file";
      }
    } catch (e) {
      // If URL parsing fails, try to extract filename from the string directly
      const lastSlashIndex = fileUurl.lastIndexOf("/");
      let filename =
        lastSlashIndex !== -1
          ? fileUurl.substring(lastSlashIndex + 1)
          : fileUurl;
      const dotIndex = filename.lastIndexOf(".");
      if (dotIndex !== -1) {
        filename = filename.substring(0, dotIndex);
      }
      // Remove timestamp prefix (13 digits) that was prepended for uniqueness
      if (/^\d{13}/.test(filename)) {
        filename = filename.substring(13);
      }
      return filename || "file";
    }
    return "file";
  };
  const handleRecordingCompleted = async (blob) => {
    setRecordingInProgress(false);
    const _file = new File([blob], "audio.wav", {
      type: "audio/wav",
    });
    const audioId = generateRowId();
    const result = await S3uploadUrlRequest({
      filename: `${getActiveWorkspace()?.id}/${audioId}.wav`,
      importType: "imported_audio_files",
      contentType: "wav",
    });
    const upload = await axios.request({
      method: "put",
      url: JSON.parse(result?.body)?.uploadURL,
      data: _file,
      headers: {
        "Content-Type": _file.type,
      },
      signal: controller.signal,
      onUploadProgress: (p) => {
        // if (controller.signal.aborted) return;
        setRecordingUploadInProgress(true);
        setRecordingUploadProgress(Math.round((p.loaded * 100) / p.total));
        if (p.loaded === p.total) {
          setRecordingUploadInProgress(false);
          setRecordingUploadProgress(100);
        }
      },
    });
    if (JSON.parse(result?.body).uploadURL) {
      onRecordingUploaded(
        `imported_audio_files/${getActiveWorkspace()?.id}/${audioId}.wav`,
      );
    }
  };
  const isValidFileSize = (file, mb = 5) => {
    const maxSizeInBytes = mb * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };

  // Unified file upload handler that routes files to the correct handler based on type
  const handleUnifiedFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Prevent uploads while another is in progress
    if (fileUploadInProgress) {
      AppToasty.show(t("toasts.upload.wait"), {
        type: "warning",
      });
      event.target.value = "";
      return;
    }

    // Group files by type (similar to handleDrop logic)
    const imageFiles = [];
    const videoFiles = [];
    const audioFiles = [];
    const docFiles = [];

    files.forEach((file) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      // Check by MIME type first, then by extension
      if (
        (fileType.startsWith("image/") ||
          fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)) &&
        allowImagesUpload
      ) {
        imageFiles.push(file);
      } else if (
        (fileType.startsWith("video/") || fileName.match(/\.(mp4)$/i)) &&
        allowVideosUpload
      ) {
        videoFiles.push(file);
      } else if (
        (fileType.startsWith("audio/") || fileName.match(/\.(wav|m4a)$/i)) &&
        allowAudiosUpload
      ) {
        audioFiles.push(file);
      } else if (allowFileUpload) {
        // Check if it's an allowed document type
        const allowedExtensions = [
          ".pdf",
          ".csv",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".html",
          ".txt",
          ".md",
          ".json",
          ".pptx",
          ".ppt",
        ];
        if (allowCodeUpload) {
          allowedExtensions.push(
            ".py",
            ".js",
            ".java",
            ".cpp",
            ".c",
            ".cs",
            ".php",
            ".rb",
            ".swift",
            ".go",
            ".ts",
            ".rs",
            ".kt",
            ".scala",
            ".m",
            ".r",
            ".pl",
            ".sh",
            ".sql",
            ".vb",
            ".lua",
            ".dart",
            ".groovy",
            ".jl",
            ".hs",
            ".f90",
            ".pas",
            ".elm",
            ".clj",
          );
        }

        const hasAllowedExtension = allowedExtensions.some((ext) =>
          fileName.endsWith(ext),
        );
        if (hasAllowedExtension) {
          docFiles.push(file);
        }
      }
    });

    // Handle each type of file
    if (imageFiles.length > 0) {
      // Show converting feedback for image conversion
      const needsConversion = imageFiles.some(
        (file) => file.type.startsWith("image/") && file.type !== "image/png",
      );
      if (needsConversion) {
        setFileUploadInProgress(true);
        setFileUploadStatus("converting");
        setFileUploadProgress(0);
      }

      // Convert non-PNG images to PNG
      const convertedImages = await Promise.all(
        imageFiles.map(async (file) => {
          if (file.type.startsWith("image/") && file.type !== "image/png") {
            try {
              return await convertImageToPng(file);
            } catch (error) {
              console.error("Failed to convert image:", error);
              AppToasty.show(
                t("toasts.upload.convertFailed", { fileName: file.name }),
                {
                  type: "danger",
                },
              );
              return null;
            }
          }
          return file;
        }),
      );
      const validImages = convertedImages.filter((f) => f !== null);
      if (validImages.length > 0) {
        setFileUploadStatus("uploading");
        handleBatchFilesSelection(validImages, "file");
      } else if (needsConversion) {
        // Reset if all conversions failed
        setFileUploadInProgress(false);
        setFileUploadStatus("");
      }
    }

    if (videoFiles.length > 0) {
      // Videos are handled one at a time
      if (videoFiles.length > 1) {
        AppToasty.show(t("toasts.upload.videoSingle"), {
          type: "warning",
        });
      }
      const videoFile = videoFiles[0];
      if (!isValidFileSize(videoFile, 50)) {
        AppToasty.show(t("toasts.upload.videoSize"), { type: "danger" });
      } else {
        handleFileSelection(videoFile, "video");
      }
    }

    if (audioFiles.length > 0) {
      handleBatchFilesSelection(audioFiles, "file");
    }

    if (docFiles.length > 0) {
      handleBatchFilesSelection(docFiles, "file");
    }

    // Reset the input so the same file can be selected again
    event.target.value = "";
  };

  // Determine effective agent ID and mode (use selectedAgent if one is selected, otherwise use primary)
  const effectiveAgentId = selectedAgent?.id || primaryAgentId;
  const effectiveAgentMode = selectedAgent?.mode || agentMode;
  const effectiveAgenticRAG = selectedAgent?.agenticRAG ?? agenticRAG;
  const effectiveUseInterleavedReasoning =
    selectedAgent?.useInterleavedReasoning ?? useInterleavedReasoning;

  // Memoize prompts to prevent constant re-creation
  const _prompts = useMemo(() => {
    if (generatorConfigData?.length > 0) {
      return generatorConfigData
        ?.filter((u) => {
          if (u?.availableToAgents?.includes(effectiveAgentId)) return u;
        })
        ?.filter((u) => u);
    }
    return [];
  }, [generatorConfigData, effectiveAgentId]);

  // Filter prompts based on mention query
  useEffect(() => {
    if (mentionQuery === "") {
      setFilteredPrompts(_prompts);
    } else {
      const filtered = _prompts.filter((prompt) => {
        const query = mentionQuery.toLowerCase();
        // Check if query matches any word in the interpolationString
        const interpolationWords =
          prompt.interpolationString?.toLowerCase().split(/\s+/) || [];
        return (
          interpolationWords.some((word) => word.includes(query)) ||
          prompt.label?.toLowerCase().includes(query)
        );
      });
      setFilteredPrompts(filtered);
    }
  }, [mentionQuery, _prompts]);

  // Filter tools based on tool query and agent mode (uses effective values when agent is selected)
  useEffect(() => {
    const isTfMain = primaryAgentId?.startsWith("tf_main");

    let availableTools;

    if (isTfMain) {
      // For tf_main: show all tools except browser and introspection
      const tfMainExcluded = [
        "browser",
        "introspection",
        "rag",
        "deep_rag",
        "browse_knowledge_hub",
        "read_knowledge_doc",
        "doc_by_doc_analysis",
        "email",
        "sms",
        "messaging",
        "conversation_retrieval",
        "scheduling",
        "long_term_memory",
        "extract_memory",
        "recall_memory",
      ];
      availableTools = _tools.filter(
        (tool) =>
          !tfMainExcluded.includes(tool.id) &&
          (tool.id !== "local_folder_access" || !!window?.electronAPI),
      );
    } else {
      availableTools = _tools.filter((tool) => {
        // ===== MODE FILTERING =====
        // Filter by agent mode (use effectiveAgentMode to reflect selected agent)
        if (tool.mode !== "all" && tool.mode !== effectiveAgentMode) {
          return false;
        }

        // ===== COMMUNICATION TOOLS =====
        // Email, SMS, WhatsApp require sender configuration
        if (tool.id === "email" && !agentEmailAsSender) return false;
        if (tool.id === "sms" && !agentPhoneNumberAsSender) return false;
        if (tool.id === "messaging" && !agentWhatsappNumberAsSender)
          return false;

        // ===== KNOWLEDGE & SEARCH TOOLS =====
        // Internet search requires explicit enable
        if (tool.id === "internet_search" && !agentAllowInternetSearch)
          return false;
        if (tool.id === "deep_internet_search" && !agentAllowDeepInternetSearch)
          return false;
        // RAG tools are available by default in retriever mode with topics

        // ===== MEDIA GENERATION TOOLS =====
        if (
          ["image_generation", "image_adaptation"].includes(tool.id) &&
          !agentAllowImagesGeneration
        )
          return false;
        if (tool.id === "video_generation" && !agentAllowVideosGeneration)
          return false;

        // ===== MEDIA ANALYSIS TOOLS =====
        if (
          ["analyze_image", "analyze_video", "analyze_audio"].includes(
            tool.id,
          ) &&
          !agentHasImages
        )
          return false;

        // ===== CODE EXECUTION =====
        if (tool.id === "code_interpreter" && !agentHasCode) return false;

        // ===== FILE OPERATIONS =====
        if (tool.id === "images_retrieval" && !agentHasImages) return false;
        if (tool.id === "local_folder_access" && !window?.electronAPI)
          return false;

        // ===== CANVAS TOOLS =====
        // Canvas requires Agentic Tooling (Legacy or 2.0)
        if (
          [
            "canvas",
            "canvas_write",
            "canvas_append",
            "canvas_replace",
            "canvas_read",
          ].includes(tool.id)
        ) {
          if (!effectiveAgenticRAG && !effectiveUseInterleavedReasoning)
            return false;
        }

        // ===== PLANNING TOOLS =====
        // Planning tools require Agentic Tooling
        if (
          [
            "create_plan",
            "update_task_status",
            "revise_plan",
            "submit_plan",
            "scheduling",
          ].includes(tool.id)
        ) {
          if (!effectiveAgenticRAG && !effectiveUseInterleavedReasoning)
            return false;
        }

        // ===== AGENT DELEGATION =====
        // delegate_to_agent is available when Agent Pool exists - filtered by agents prop

        // ===== BROWSER AUTOMATION =====
        // Browser requires Agentic Tooling AND VM configured
        if (tool.id === "browser") {
          if (!effectiveAgenticRAG && !effectiveUseInterleavedReasoning)
            return false;
          if (!agentDesktopVM) return false;
        }

        // ===== INTROSPECTION =====
        // Introspection requires Retriever mode + Agentic Tooling
        if (tool.id === "introspection") {
          if (effectiveAgentMode !== "retriever") return false;
          if (!effectiveAgenticRAG && !effectiveUseInterleavedReasoning)
            return false;
        }

        // ===== USER INTERACTION =====
        if (tool.id === "ask_user_question" && !agentAllowAskQuestion)
          return false;
        // notify_user is always available

        // ===== CHARTING =====
        if (tool.id === "charting" && !agentCharting) return false;

        // ===== DEEP RAG =====
        // Deep RAG requires Agentic Tooling
        if (
          tool.id === "deep_rag" &&
          !effectiveAgenticRAG &&
          !effectiveUseInterleavedReasoning
        )
          return false;

        return true;
      });

      // Add browser tool if conditions are met (use effectiveAgenticRAG to reflect selected agent)
      if (effectiveAgenticRAG && agentDesktopVM) {
        const browserTool = _tools.find((tool) => tool.id === "browser");
        if (browserTool && !availableTools.find((t) => t.id === "browser")) {
          availableTools = [...availableTools, browserTool];
        }
      }
    }

    // Deduplicate by id (keep first occurrence)
    const seenIds = new Set();
    availableTools = availableTools.filter((tool) => {
      if (seenIds.has(tool.id)) return false;
      seenIds.add(tool.id);
      return true;
    });

    // Map tools to use name for display label (preserve original id)
    availableTools = availableTools.map((tool) => ({
      ...tool,
      label: tool.name,
    }));

    // Add custom functions from availableFunctions
    const customFunctions = availableFunctions.map((fn) => ({
      id: fn?.name,
      name: fn?.name,
      label: fn?.name,
      description: fn?.description || fn?.name,
      mode: "all",
    }));

    const allTools = [...availableTools, ...customFunctions];

    if (toolQuery === "") {
      setFilteredTools(allTools);
    } else {
      const filtered = allTools.filter((tool) => {
        const query = toolQuery.toLowerCase();
        return (
          tool.id?.toLowerCase().includes(query) ||
          tool.name?.toLowerCase().includes(query)
        );
      });
      setFilteredTools(filtered);
    }
  }, [
    toolQuery,
    effectiveAgentMode,
    effectiveAgenticRAG,
    effectiveUseInterleavedReasoning,
    agentDesktopVM,
    agentCharting,
    agentAllowInternetSearch,
    agentAllowDeepInternetSearch,
    agentAllowImagesGeneration,
    agentAllowVideosGeneration,
    agentHasCode,
    agentHasImages,
    agentEmailAsSender,
    agentPhoneNumberAsSender,
    agentWhatsappNumberAsSender,
    agentAllowAskQuestion,
    availableFunctions,
    primaryAgentId,
  ]);

  // Filter agents based on agent query (# trigger)
  // Agent switching is disabled in planner and voice modes
  useEffect(() => {
    // Don't allow agent switching in planner or voice mode
    if (agentMode === "planner" || agentMode === "voice") {
      setFilteredAgents([]);
      return;
    }

    // Exclude current primary agent from the list
    const availableAgents = agents.filter(
      (agent) => agent?.id !== primaryAgentId,
    );

    if (agentQuery === "") {
      setFilteredAgents(availableAgents);
    } else {
      const filtered = availableAgents.filter((agent) => {
        const query = agentQuery.toLowerCase();
        return (
          agent?.label?.toLowerCase().includes(query) ||
          agent?.description?.toLowerCase().includes(query)
        );
      });
      setFilteredAgents(filtered);
    }
  }, [agentQuery, agents, primaryAgentId, agentMode]);

  // get focus on input when component mounts
  React.useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Handle aborted drag operations
  React.useEffect(() => {
    const handleDragEnd = () => {
      setIsDragging(false);
    };

    // Listen for dragend on document to catch aborted drags
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, []);

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we're actually leaving the drop zone
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    // If the mouse is outside the bounds of the drop zone, hide the overlay
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Check if the component is disabled
    if (
      (showWidgetChatsHistory || canInteractWithChat === false) &&
      handedOffToHuman !== true
    ) {
      return;
    }

    // Check if any upload is in progress
    if (
      audioProcessingInProgress ||
      fileUploadInProgress ||
      isSpeechGettingProcessed
    ) {
      AppToasty.show(t("toasts.upload.waitOperation"), {
        type: "warning",
      });
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Group files by type
    const imageFiles = [];
    const videoFiles = [];
    const audioFiles = [];
    const docFiles = [];

    files.forEach((file) => {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      // Check by MIME type first, then by extension
      if (
        (fileType.startsWith("image/") ||
          fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i)) &&
        allowImagesUpload
      ) {
        imageFiles.push(file);
      } else if (
        (fileType.startsWith("video/") ||
          fileName.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)$/i)) &&
        allowVideosUpload
      ) {
        videoFiles.push(file);
      } else if (
        (fileType.startsWith("audio/") ||
          fileName.match(/\.(mp3|wav|ogg|m4a|flac|aac|wma)$/i)) &&
        allowAudiosUpload
      ) {
        audioFiles.push(file);
      } else if (allowFileUpload) {
        // Check if it's an allowed document type
        const allowedExtensions = [
          ".pdf",
          ".csv",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".html",
          ".txt",
          ".md",
          ".json",
        ];
        if (allowCodeUpload) {
          allowedExtensions.push(
            ".py",
            ".js",
            ".java",
            ".cpp",
            ".c",
            ".cs",
            ".php",
            ".rb",
            ".swift",
            ".go",
            ".ts",
            ".rs",
            ".kt",
            ".scala",
            ".m",
            ".r",
            ".pl",
            ".sh",
            ".sql",
            ".vb",
            ".lua",
            ".dart",
            ".groovy",
            ".jl",
            ".hs",
            ".f90",
            ".pas",
            ".elm",
            ".clj",
          );
        }

        allowedExtensions.push(".pptx", ".ppt");
        // Don't add .png here as it should be handled by image files

        const hasAllowedExtension = allowedExtensions.some((ext) =>
          fileName.endsWith(ext),
        );
        if (hasAllowedExtension) {
          docFiles.push(file);
        }
      }
    });

    // Handle each type of file
    if (imageFiles.length > 0) {
      handleBatchFilesSelection(imageFiles, "file");
    }

    if (videoFiles.length > 0) {
      // Videos are handled one at a time
      if (videoFiles.length > 1) {
        AppToasty.show(t("toasts.upload.videoSingle"), {
          type: "warning",
        });
      }
      const videoFile = videoFiles[0];
      if (!isValidFileSize(videoFile, 50)) {
        AppToasty.show(t("toasts.upload.videoSize"), { type: "danger" });
      } else {
        handleFileSelection(videoFile, "video");
      }
    }

    if (audioFiles.length > 0) {
      handleBatchFilesSelection(audioFiles, "file");
    }

    if (docFiles.length > 0) {
      handleBatchFilesSelection(docFiles, "file");
    }
  };

  // Handle clipboard paste for images
  // Mac/clipboard pastes name images generically ("image"): number them so
  // consecutive pastes don't collide as the same file (image 1, image 2, ...).
  // The counter is assigned by the paste handler so the inline <image N> token
  // always matches the uploaded file name.
  const renamePastedGenericImage = (file, counter) => {
    const dotIndex = file?.name?.lastIndexOf(".") ?? -1;
    const baseName =
      dotIndex > 0 ? file.name.substring(0, dotIndex) : file.name;
    if (baseName?.toLowerCase() !== "image") return file;
    const extension = dotIndex > 0 ? file.name.substring(dotIndex) : ".png";
    return new File([file], `image ${counter}${extension}`, {
      type: file.type,
    });
  };

  // Inserts a token at the caret: a chip in the chip composer, plain text in
  // the fallback textarea.
  const insertToken = (token) => {
    if (textareaRef.current?.insertToken) {
      textareaRef.current.insertToken(token);
    } else {
      insertTokenAtCursor(textareaRef.current, token, setText);
    }
  };

  // Clipboard capture: rich/large text becomes a file attachment referenced
  // inline with a <clipboard N> token; pure image pastes upload the image and
  // reference it with an <image N> token. Shared by the document-level
  // listener (fallback textarea mode) and the composer's onPaste.
  const handleClipboardPasteCapture = (event) => {
    if (!allowImagesUpload && !allowFileUpload) return;

    const { htmlString, plainString, imageItem } = extractClipboardParts(
      event.clipboardData,
    );
    if (!htmlString && !plainString && !imageItem) return;

    // Pure image paste (screenshot, copied image - no text flavours):
    // upload as a file and reference it inline with an <image N> token.
    if (imageItem && !htmlString && !plainString) {
      if (!allowImagesUpload) return;
      event.preventDefault();

      const file = imageItem.getAsFile();
      if (!file) return;

      // Check file size (same validation as regular image upload)
      if (!isValidFileSize(file)) {
        AppToasty.show(t("toasts.upload.imageSize"), {
          type: "danger",
        });
        return;
      }

      const imageCounter = ++pastedImageCounterRef.current;

      // Handle the pasted image file. Normalise non-PNG clipboard formats
      // (Mac screenshots can arrive as TIFF) to PNG first so downstream
      // classification by extension works.
      (async () => {
        let fileToUpload = file;
        if (file.type?.startsWith("image/") && file.type !== "image/png") {
          try {
            fileToUpload = await convertImageToPng(file);
          } catch (error) {
            console.error("Failed to convert pasted image:", error);
          }
        }
        const renamed = renamePastedGenericImage(fileToUpload, imageCounter);
        capturedFileNamesRef.current.add(normalizeCaptureFileName(renamed.name));
        handleBatchFilesSelection([renamed], "file");
      })();
      insertToken(`<image ${imageCounter}>`);
      return;
    }

    // Rich/large text paste (Office suite, web pages, big plain text):
    // capture the clipboard content as a file attachment and reference it
    // inline with a <clipboard N> token instead of dumping a raw text blob
    // into the input.
    if (!allowFileUpload) return;
    if (!shouldCaptureAsFile({ htmlString, plainString })) return;

    event.preventDefault();
    const clipboardCounter = ++pastedClipboardCounterRef.current;
    const source = detectClipboardSource(htmlString);
    let file = buildClipboardFile({
      htmlString,
      plainString,
      counter: clipboardCounter,
    });

    // Excel pastes become real .xlsx files (SheetJS converts the Office HTML
    // table); other sources keep the HTML capture which preserves formatting.
    if (source === "excel" && htmlString) {
      try {
        file = excelHtmlToXlsxFile(htmlString, `clipboard ${clipboardCounter}`);
      } catch (error) {
        console.error("Failed to convert Excel paste to xlsx:", error);
      }
    }

    // Same size validation as regular file uploads
    if (file.size > fileSizeConstant * 1024 * 1024) {
      AppToasty.show(
        t("toasts.upload.fileSizeExceeded", {
          fileName: file.name,
          maxSize: fileSizeConstant,
        }),
        { type: "danger" },
      );
      return;
    }

    handleBatchFilesSelection([file], "file");
    capturedFileNamesRef.current.add(normalizeCaptureFileName(file.name));
    setClipboardLabels((previous) => ({
      ...previous,
      [`<clipboard ${clipboardCounter}>`]: clipboardCaptureLabel(
        source,
        clipboardCounter,
      ),
    }));
    insertToken(`<clipboard ${clipboardCounter}>`);
  };

  // Keep the latest handler in a ref so the document-level listener stays
  // stable while always seeing current props/state.
  const pasteHandlerRef = useRef();
  pasteHandlerRef.current = handleClipboardPasteCapture;

  useEffect(() => {
    const listener = (event) => {
      // Pastes aimed at another editable element (search box, modal field,
      // another Arena pane) belong to that element - leave them alone. The
      // composer handles its own pastes via onPaste + stopPropagation (this
      // listener never sees them), so this document-level path serves only
      // the fallback textarea and "paste anywhere with nothing focused".
      const editableTarget = event.target?.closest?.(
        'input, textarea, [contenteditable]:not([contenteditable="false"])',
      );
      if (editableTarget && event.target !== textareaRef.current) return;
      pasteHandlerRef.current?.(event);
    };
    document.addEventListener("paste", listener);
    return () => {
      document.removeEventListener("paste", listener);
    };
  }, []);

  // Composer pastes are handled element-scoped; stop propagation so the
  // document listener does not double-handle them.
  const handleComposerPaste = (event) => {
    pasteHandlerRef.current?.(event);
    event.stopPropagation();
  };
  const getUrl = async (path, type = "image") => {
    let initialPath = "";
    setFileExtension(path?.split(".")?.pop());
    if (type == "image") {
      initialPath = "imported-image";
    } else if (type == "audio") {
      initialPath = "imported_audio_files";
    } else if (type == "video") {
      initialPath = "imported_video_files";
    } else if (type == "file") {
      initialPath = "imported_doc_files";
    } else {
      return "";
    }
    const _url = await S3downloadUrlRequest({
      filename: `${initialPath}/${getActiveWorkspace()?.id}/${path}`,
      context: "pdf",
      workspaceid: getActiveWorkspace()?.id,
    });
    if (_url?.url && type == "image") setImageUrl(_url?.url);
    if (_url?.url && type == "video") setVideoUrl(_url?.url);
    if (_url?.url && type == "audio") setAudioUrl(_url?.url);
    if (_url?.url && type == "file") return _url?.url;
  };

  const handleBatchFilesSelection = async (files, type = "file") => {
    try {
      if (!files) return;

      const filesArray = Array.from(files);
      const uploadedFilesUrls = [];
      const presignedFilesUrls = [];

      // Validate before setting upload in progress
      if (
        filesArray?.length > 5 ||
        filesArray?.length + (fileUrls?.length || 0) > 5
      ) {
        AppToasty.show(t("toasts.upload.fileLimit"), {
          type: "danger",
        });
        return;
      }

      // Check file size (fileSizeConstant MB)
      const maxFileSize = fileSizeConstant * 1024 * 1024;
      for (let i = 0; i < filesArray.length; i++) {
        if (filesArray[i].size > maxFileSize) {
          AppToasty.show(
            t("toasts.upload.fileSizeExceeded", {
              fileName: filesArray[i].name,
              maxSize: fileSizeConstant,
            }),
            {
              type: "danger",
            },
          );
          return;
        }
      }

      // Initialize progress tracking only after validation passes
      // Only set if not already in progress (e.g., from image conversion)
      if (!fileUploadInProgress) {
        setFileUploadInProgress(true);
        setFileUploadProgress(0);
      }
      setFileUploadStatus("uploading");

      const totalFiles = filesArray.length;

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        let filename = file.name;
        filename = filename.replace(/[^a-zA-Z0-9.]/g, "");
        const _fileN = String(Date.now()) + filename;

        if (filename?.length > 100) {
          setFileUploadInProgress(false);
          setFileUploadProgress(0);
          return AppToasty.show(t("toasts.upload.fileNameLength"), {
            type: "danger",
          });
        }
        const contentType = filename.slice(
          (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1,
        );
        let importType = "imported-image";
        if (type === "video") {
          importType = "imported_video_files";
        } else if (type === "audio") {
          importType = "imported_audio_files";
        } else if (type === "file") {
          importType = "imported_doc_files";
        }
        const result = await S3uploadUrlRequest({
          filename: `${getActiveWorkspace()?.id}/${_fileN}`,
          importType,
          contentType,
        });

        await axios.request({
          method: "put",
          url: JSON.parse(result?.body)?.uploadURL,
          data: file,
          headers: { "Content-Type": file.type },
          cancelToken: source.token,
          onUploadProgress: (p) => {
            const currentFileProgress = Math.round((p.loaded * 100) / p.total);
            // Calculate overall progress: completed files + current file progress
            // Each file contributes (100 / totalFiles) to the overall progress
            const completedFilesProgress = (i * 100) / totalFiles;
            const currentFileContribution = currentFileProgress / totalFiles;
            const overallProgress = Math.round(
              completedFilesProgress + currentFileContribution,
            );
            setFileUploadProgress(overallProgress);
          },
        });
        if (JSON.parse(result?.body).uploadURL) {
          const _url = await getUrl(_fileN, type);
          uploadedFilesUrls.push(
            `imported_doc_files/${getActiveWorkspace()?.id}/${_fileN}`,
          );
          presignedFilesUrls.push(_url);
        }
      }

      // Live session: attachments go straight to the chat as context
      // (the queued-for-next-send path can't work while voice is active)
      if (isLiveKitMode && liveSessionId) {
        if (uploadedFilesUrls.length > 0) {
          await shareAttachmentLive(uploadedFilesUrls, "file");
        }
        return;
      }

      onFilesUploaded(uploadedFilesUrls, presignedFilesUrls);
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setFileUploadInProgress(false);
      setFileUploadProgress(0);
      setFileUploadStatus("");
    }
  };

  // During a live session, attachments become chat context via the /live
  // service (not queued for the next text send) + a realtime data message
  // so the voice agent knows immediately.
  const shareAttachmentLive = async (keys, type) => {
    if (!liveSessionId) return false;
    // The unified upload routes everything through the doc path
    // (imported_doc_files) — classify by extension so each attachment is
    // stored under the right field (images/audios/videos/files) and the
    // agent's read_attachment + ChatECS analyze_* tools recognize them.
    const EXT_MAP = [
      ["images", /\.(png|jpe?g|gif|bmp|webp|svg|tiff?|heic|heif|ico)$/i],
      ["audios", /\.(mp3|wav|ogg|m4a|aac|flac|wma)$/i],
      ["videos", /\.(mp4|avi|mov|webm|mkv|flv|m4v)$/i],
    ];
    const classify = (k, t) => {
      if (t === "image") return "images";
      if (t === "audio") return "audios";
      if (t === "video") return "videos";
      const hit = EXT_MAP.find(([, re]) => re.test(k));
      return hit ? hit[0] : "files";
    };
    const groups = { images: [], audios: [], videos: [], files: [] };
    (keys || []).forEach((k, i) => groups[classify(k, type)].push(k));
    const payload = {};
    Object.entries(groups).forEach(([field, arr]) => {
      if (arr.length) payload[field] = arr;
    });
    const total = Object.values(groups).flat();
    try {
      const response = await Live.createLiveAttachments({
        token: apiKey,
        sessionId: liveSessionId,
        ...payload,
        userId: "web-user",
      });
      if (!response?.ok) {
        throw new Error(
          response?.data?.errorMessage ||
            `Attachment failed (${response?.status})`,
        );
      }
      // Realtime heads-up to the agent (room must be connected)
      await sendLiveKitData({ type: "attachment_added", ...payload });
      // Surface the stored attachment message immediately — the backend
      // stores it as a chat message, but the AppSync event doesn't always
      // reach this client mid-voice-session, so pull it explicitly
      // (same refresh the voice-stop handler does).
      onVoiceSessionEnded();
      AppToasty.show(t("toasts.liveMode.attachmentShared"), {
        type: "success",
        duration: 2500,
      });
      return true;
    } catch (error) {
      console.error("[LiveMode] Attachment sharing failed:", error);
      AppToasty.show(
        error?.message || t("toasts.liveMode.attachmentFailed"),
        { type: "danger", duration: 4000 },
      );
      return false;
    }
  };

  const handleFileSelection = async (file, type = "image") => {
    if (!file) return;

    let filename = file.name;
    filename = filename.replace(/[^a-zA-Z0-9.]/g, "");
    const _fileN = String(Date.now()) + filename;

    if (filename?.length > 100) {
      return AppToasty.show(t("toasts.upload.fileNameLength"), {
        type: "danger",
      });
    }

    // Initialize progress tracking before upload starts
    setFileUploadInProgress(true);
    setFileUploadProgress(0);

    try {
      const contentType = filename.slice(
        (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1,
      );
      let importType = "imported-image";
      if (type === "video") {
        importType = "imported_video_files";
      } else if (type === "audio") {
        importType = "imported_audio_files";
      } else if (type === "file") {
        importType = "imported_doc_files";
      }

      const result = await S3uploadUrlRequest({
        filename: `${getActiveWorkspace()?.id}/${_fileN}`,
        importType,
        contentType,
      });

      await axios.request({
        method: "put",
        url: JSON.parse(result?.body)?.uploadURL,
        data: file,
        headers: {
          "Content-Type": file.type,
        },
        cancelToken: source.token,
        onUploadProgress: (p) => {
          setFileUploadProgress(Math.round((p.loaded * 100) / p.total));
        },
      });

      if (JSON.parse(result?.body).uploadURL) {
        const _url = getUrl(_fileN, type);

        // Live session: attachment goes straight to the chat as context
        if (isLiveKitMode && liveSessionId) {
          await shareAttachmentLive(
            [`imported-image/${getActiveWorkspace()?.id}/${_fileN}`],
            "image",
          );
          return;
        }

        if (type === "image") {
          onImageUploaded(
            `imported-image/${getActiveWorkspace()?.id}/${_fileN}`,
          );
          setImageUrl(_url);
        }
        if (type === "audio") {
          onAudioUploaded(
            `imported_audio_files/${getActiveWorkspace()?.id}/${_fileN}`,
          );
        }
        if (type === "video") {
          onVideoUploaded(
            `imported_video_files/${getActiveWorkspace()?.id}/${_fileN}`,
          );
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setFileUploadInProgress(false);
      setFileUploadProgress(0);
      setFileUploadStatus("");
    }
  };
  function getPlaceholderText() {
    if (handedOffToHuman) return t("chats.placeholders.handedOffToHuman");
    else if (showWidgetChatsHistory || canInteractWithChat === false) {
      return t("chats.placeholders.cannotInteract");
    } else if (isAwaitingUserInput) {
      return t("chats.placeholders.waitingForResponse");
    } else if (audioProcessingInProgress) {
      return t("chats.placeholders.processingAudio");
    } else if (fileUploadInProgress) {
      if (fileUploadStatus === "converting") {
        return t("chats.placeholders.preparingFiles");
      }
      return t("chats.placeholders.uploadingFiles");
    } else if (recordingInProgress) {
      return t("chats.placeholders.listening");
    } else if (isSpeechGettingProcessed) {
      return t("chats.placeholders.generatingSpeech");
    } else if (
      forcePlannerStop &&
      planningExecution != "plan_execution_failed"
    ) {
      return t("chats.placeholders.stoppingPlan");
    } else if (planningExecution == "step_requires_approval")
      return t("chats.placeholders.approveStep");
    else if (planningExecution == "planning_requires_approval")
      return t("chats.placeholders.approvePlan");
    else if (planningExecution == "step_requires_desktop")
      return t("chats.placeholders.desktopExecuting");
    else if (
      isPlannerAcceptingFeedback &&
      planningExecution == "plan_execution_in_progress"
    ) {
      return t("chats.placeholders.provideFeedback");
    } else if (planningExecution == "plan_execution_in_progress") {
      return t("chats.placeholders.planExecutionInProgress");
    } else if (recordingUploadInProgress) {
      return t("chats.placeholders.transcriptionInProgress");
    } else {
      return placeholderText;
    }
  }

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      const handleScroll = () => {
        const currentScrollHeight = textarea.scrollHeight;
        const scrollTop = textarea.scrollTop;

        if (textarea.scrollTop < prevScrollTop) {
          // Scrolling up
          setIsScrollingUp(true);
        } else {
          // Scrolling down
          setIsScrollingUp(false);
        }

        // Update the previous scrollTop value
        setPrevScrollTop(textarea.scrollTop);
        // If the user is scrolling up, remove the scroll event listener
        if (prevScrollHeight > currentScrollHeight || isScrollingUp) {
          textarea.removeEventListener("scroll", handleScroll);
          setPrevScrollHeight(currentScrollHeight);
          return;
        }

        // If the user is typing, set the scroll position to the bottom
        if (isTyping) {
          textarea.scrollTop = currentScrollHeight;
          setPrevScrollHeight(currentScrollHeight);
        }
      };

      textarea.addEventListener("scroll", handleScroll);
      textarea.addEventListener("input", () => setIsTyping(true));
      textarea.addEventListener("compositionstart", () => setIsTyping(true));
      textarea.addEventListener("compositionend", () => setIsTyping(false));

      // Set the initial scroll position to the bottom
      textarea.scrollTop = textarea.scrollHeight;
      setPrevScrollHeight(textarea.scrollHeight);

      return () => {
        textarea.removeEventListener("scroll", handleScroll);
        textarea.removeEventListener("input", () => setIsTyping(true));
        textarea.removeEventListener("compositionstart", () =>
          setIsTyping(true),
        );
        textarea.removeEventListener("compositionend", () =>
          setIsTyping(false),
        );
      };
    }
  }, [isTyping]);

  // Shared input-change logic (value + caret offset), fed by the fallback
  // textarea's onInput and by the chip composer's onChange.
  const processInputChange = (value, cursorPos) => {
    setText(value);

    // Show tips when input is empty, hide when there's text
    if (value.length > 0) {
      setHasStartedTyping(true);
      setShowTip(false);
    } else {
      setHasStartedTyping(false);
      setShowTip(true);
    }

    setCursorPosition(cursorPos);
    const textBeforeCursor = value.substring(0, cursorPos);

    // Check for ~, @, and # triggers
    const lastTildeIndex = textBeforeCursor.lastIndexOf("~");
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    const lastHashIndex = textBeforeCursor.lastIndexOf("#");

    // Helper function to check if trigger is active
    const isTriggerActive = (triggerIndex, textAfterTrigger) => {
      if (triggerIndex === -1) return false;
      const spaceIndex = textAfterTrigger.indexOf(" ");
      const newlineIndex = textAfterTrigger.indexOf("\n");
      if (spaceIndex === -1 && newlineIndex === -1) return true;
      const firstBreakIndex =
        spaceIndex !== -1 && newlineIndex !== -1
          ? Math.min(spaceIndex, newlineIndex)
          : spaceIndex !== -1
          ? spaceIndex
          : newlineIndex;
      return textAfterTrigger.length <= firstBreakIndex;
    };

    // Find the most recent trigger
    const maxIndex = Math.max(lastTildeIndex, lastAtIndex, lastHashIndex);

    // Reset all dropdowns first
    const resetAllDropdowns = () => {
      setShowToolDropdown(false);
      setToolQuery("");
      setShowMentionDropdown(false);
      setMentionQuery("");
      setShowAgentDropdown(false);
      setAgentQuery("");
    };

    if (maxIndex === -1) {
      resetAllDropdowns();
    } else if (
      maxIndex === lastHashIndex &&
      agentMode !== "planner" &&
      agentMode !== "voice"
    ) {
      // # agent selection mode (disabled in planner and voice modes)
      const textAfterHash = textBeforeCursor.substring(lastHashIndex + 1);
      if (isTriggerActive(lastHashIndex, textAfterHash)) {
        setAgentStartPosition(lastHashIndex);
        setAgentQuery(textAfterHash);
        setShowAgentDropdown(true);
        setShowToolDropdown(false);
        setToolQuery("");
        setShowMentionDropdown(false);
        setMentionQuery("");
      } else {
        resetAllDropdowns();
      }
    } else if (maxIndex === lastTildeIndex) {
      // ~ tool selection mode
      const textAfterTilde = textBeforeCursor.substring(lastTildeIndex + 1);
      if (isTriggerActive(lastTildeIndex, textAfterTilde)) {
        setToolStartPosition(lastTildeIndex);
        setToolQuery(textAfterTilde);
        setShowToolDropdown(true);
        setShowMentionDropdown(false);
        setMentionQuery("");
        setShowAgentDropdown(false);
        setAgentQuery("");
      } else {
        resetAllDropdowns();
      }
    } else if (maxIndex === lastAtIndex) {
      // @ mention mode
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (isTriggerActive(lastAtIndex, textAfterAt)) {
        setMentionStartPosition(lastAtIndex);
        setMentionQuery(textAfterAt);
        setShowMentionDropdown(true);
        setShowToolDropdown(false);
        setToolQuery("");
        setShowAgentDropdown(false);
        setAgentQuery("");
      } else {
        resetAllDropdowns();
      }
    } else {
      resetAllDropdowns();
    }

    setIsInputEmpty(value.length < 10);
  };

  // Fallback textarea onInput
  const handleTextareaInput = (event) =>
    processInputChange(event.target.value, event.target.selectionStart);

  // Chip composer onChange
  const handleComposerInput = ({ text: nextText, selectionStart }) =>
    processInputChange(nextText, selectionStart);

  const handleMentionSelect = (prompt) => {
    const textarea = textareaRef.current;
    const currentValue = textarea.value;
    const beforeMention = currentValue.substring(0, mentionStartPosition);
    const afterMention = currentValue.substring(cursorPosition);

    const newValue = beforeMention + prompt.interpolationString + afterMention;
    textarea.value = newValue;

    const newCursorPosition =
      beforeMention.length + prompt.interpolationString.length;
    textarea.setSelectionRange(newCursorPosition, newCursorPosition);

    setShowMentionDropdown(false);
    setMentionQuery("");
    textarea.focus();
  };

  const handleToolSelect = (tool) => {
    const textarea = textareaRef.current;
    const currentValue = textarea.value;
    const beforeTool = currentValue.substring(0, toolStartPosition);
    const afterTool = currentValue.substring(cursorPosition);

    // Check if tool is already selected (derived from the input text)
    if (selectedToolIds.includes(tool.id)) {
      AppToasty.show(t("toasts.tools.alreadySelected"), { type: "warning" });
      setShowToolDropdown(false);
      setToolQuery("");
      textarea.focus();
      return;
    }

    // Replace the ~ and query with the tool tag in textarea (selection state
    // is derived from the text)
    const toolTag = `~${tool.id}~`;
    const newValue = beforeTool + toolTag + " " + afterTool;
    textarea.value = newValue;
    setText(newValue);
    const newCursorPosition = beforeTool.length + toolTag.length + 1;
    textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    setShowToolDropdown(false);
    setToolQuery("");
    textarea.focus();
  };

  const handleAgentSelect = (agent) => {
    const textarea = textareaRef.current;
    const currentValue = textarea.value;
    const agentMention = `@${agent.label || agent.name || agent.id}`;

    // The # selection becomes an "@Label" mention pill in the text (home mode
    // always worked this way; normal mode now does too). Outside home mode
    // only one delegation is active at a time, so remove any existing
    // mention before inserting the new one.
    let baseValue = currentValue;
    let insertPos = agentStartPosition;
    let endPos = cursorPosition;
    if (!isHomeMode) {
      const mentionRegex = buildAgentMentionRegex(agentLabels, "g");
      if (mentionRegex) {
        const ranges = [];
        let match;
        while ((match = mentionRegex.exec(currentValue)) !== null) {
          const mentionEnd =
            match.index +
            match[1].length +
            (currentValue[match.index + match[1].length] === " " ? 1 : 0);
          // leave anything overlapping the active #query span alone
          if (match.index < cursorPosition && mentionEnd > agentStartPosition)
            continue;
          ranges.push([match.index, mentionEnd]);
        }
        let result = "";
        let last = 0;
        ranges.forEach(([start, end]) => {
          result += currentValue.slice(last, start);
          const removedLength = end - start;
          if (start < insertPos) {
            insertPos -= removedLength;
            endPos -= removedLength;
          } else if (start < endPos) {
            endPos -= removedLength;
          }
          last = end;
        });
        baseValue = result + currentValue.slice(last);
      }
    }

    const newValue =
      baseValue.slice(0, insertPos) +
      agentMention +
      " " +
      baseValue.slice(endPos);
    textarea.value = newValue;
    setText(newValue);
    const newCursorPosition = insertPos + agentMention.length + 1;
    textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    setShowAgentDropdown(false);
    setAgentQuery("");
    textarea.focus();
  };

  // Folder binding functions
  const bindFolder = (folderPath) => {
    const folderName = folderPath.split(/[\\/]/).pop() || "Selected Folder";

    setBoundFolderPath(folderPath);
    setBoundFolderName(folderName);

    onFolderBound({
      path: folderPath,
      name: folderName,
      metadata: {
        boundFolderPath: folderPath,
        boundFolderName: folderName,
        boundFolderTimestamp: Date.now(),
      },
    });

    const folderTool = _tools.find((tool) => tool.id === "local_folder_access");
    if (folderTool && !selectedToolIds.includes("local_folder_access")) {
      const textarea = textareaRef.current;
      if (textarea) {
        const currentValue = textarea.value;
        const toolTag = `~local_folder_access~`;
        const newValue = currentValue ? `${currentValue} ${toolTag}` : toolTag;
        textarea.value = newValue;
        setText(newValue);
        textarea.focus();
      }
    }

    onUpdateRecentFolders(folderPath);
  };

  // Folder binding lives in the message text as the ~local_folder_access~
  // pill; if the user deletes that pill (backspace or its inline ✕), unbind
  // the folder so state and text never disagree.
  useEffect(() => {
    if (boundFolderPath && !(text || "").includes("~local_folder_access~")) {
      window?.electronAPI?.writeLog?.(
        "info",
        "folder auto-unbound: ~local_folder_access~ pill removed from message text",
        { path: boundFolderPath },
      );
      handleUnbindFolder();
    }
  }, [text, boundFolderPath]);

  const handleSelectRecentFolder = (folderPath) => {
    setShowFolderDropdown(false);
    bindFolder(folderPath);
  };

  const handleSelectFolder = async () => {
    if (!allowFolderBinding) {
      AppToasty.show(t("toasts.folder.bindingNotAvailable"), {
        type: "warning",
      });
      return;
    }

    setShowFolderDropdown(false);

    try {
      setIsFolderSelectionOpen(true);

      let folderPath;

      if (window?.electronAPI?.selectFolder) {
        const result = await window.electronAPI.selectFolder();

        if (result && result.success && result.path) {
          folderPath = result.path;
        } else if (result && result.canceled) {
          return;
        } else if (result && result.error) {
          throw new Error(result.error);
        }
      } else {
        folderPath = "/Users/gabriele_sanguigno/TF_coding/app/shared";
        console.log(
          "Test mode: Using mock folder path for folder binding:",
          folderPath,
        );
      }

      if (folderPath) {
        bindFolder(folderPath);
      }
    } catch (error) {
      console.error("Error selecting folder:", error);
      window?.electronAPI?.writeLog?.("error", "folder select failed", {
        error: error?.message,
      });
      AppToasty.show(t("toasts.folder.selectFailed"), { type: "danger" });
    } finally {
      setIsFolderSelectionOpen(false);
    }
  };

  const handleUnbindFolder = () => {
    if (boundFolderPath) {
      const previousPath = boundFolderPath;
      const previousName = boundFolderName;

      setBoundFolderPath(null);
      setBoundFolderName(null);

      // Remove tool tag from textarea (tool selection is derived from text)
      const textarea = textareaRef.current;
      if (textarea) {
        const currentValue = textarea.value;
        const toolTag = `~local_folder_access~`;
        const newValue = currentValue.replace(
          new RegExp(`${toolTag.replace(/~/g, "\\~")}\\s?`, "g"),
          "",
        );
        textarea.value = newValue;
        setText(newValue);
        textarea.focus();
      }

      // Notify parent component to update chat metadata
      onFolderUnbound({
        path: previousPath,
        name: previousName,
        // Clear folder metadata in chat
        metadata: {
          boundFolderPath: null,
          boundFolderName: null,
          boundFolderTimestamp: null,
        },
      });

      // AppToasty.show(`Folder "${previousName}" unbound`, {
      //   type: "info",
      // });
    }
  };
  useEffect(() => {
    if ((isAwaitingUserInput, structuredQuestions)) {
      setIsExpanded(true);
    }
  }, [isAwaitingUserInput, structuredQuestions]);

  const handleKeyDown = async (event) => {
    // Shift + Tab to toggle plan mode (only when agenticRAG or useInterleavedReasoning is enabled and not forced)
    // Prevent toggling when awaiting user input
    if (
      event.key === "Tab" &&
      event.shiftKey &&
      (effectiveAgenticRAG || effectiveUseInterleavedReasoning) &&
      !forcePlanMode &&
      !isAwaitingUserInput
    ) {
      event.preventDefault();
      onPlanModeToggle(!planMode);
      return;
    }

    if (showMentionDropdown) {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        // Handle arrow navigation in mention dropdown
        return;
      }
      if (event.key === "Enter" && filteredPrompts.length > 0) {
        event.preventDefault();
        handleMentionSelect(filteredPrompts[0]);
        return;
      }
    }
    if (showToolDropdown) {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowToolDropdown(false);
        setToolQuery("");
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        // Handle arrow navigation in tool dropdown
        return;
      }
      if (event.key === "Enter" && filteredTools.length > 0) {
        event.preventDefault();
        handleToolSelect(filteredTools[0]);
        return;
      }
    }
    if (showAgentDropdown) {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowAgentDropdown(false);
        setAgentQuery("");
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        // Handle arrow navigation in agent dropdown
        return;
      }
      if (event.key === "Enter" && filteredAgents.length > 0) {
        event.preventDefault();
        handleAgentSelect(filteredAgents[0]);
        return;
      }
    }

    // When expanded: Shift+Enter sends, Enter creates new line
    // When not expanded: Enter sends, Shift+Enter creates new line
    const shouldSend = isExpanded
      ? event.key === "Enter" && event.shiftKey
      : event.key === "Enter" && !event.shiftKey;

    if (
      shouldSend &&
      (!isStreaming ||
        isPlannerAcceptingFeedback ||
        isAgenticRAGAcceptingFeedback) &&
      !audioProcessingInProgress &&
      !fileUploadInProgress
    ) {
      event.preventDefault();
      const currentValue = textareaRef.current?.value ?? "";
      if (currentValue.length > 30000) {
        setIsInputEmpty(false);
        return AppToasty.show(t("toasts.characters.maxLimit"), {
          type: "danger",
        });
      }
      // Delegation is text-driven: the "@Agent Label" mention pill picks the
      // agent (normal mode) and is stripped from the sent text; home mode
      // keeps mentions in the text for downstream multi-agent routing
      const { text: outgoingText, agent: agentForSend } =
        deriveAgentRouting(currentValue);
      // If an agent was mentioned, use bundled send to avoid race conditions
      if (agentForSend && onSendWithAgent) {
        onSendWithAgent(agentForSend, outgoingText);
      } else {
        if (agentForSend) {
          await onAgentSelected(agentForSend);
        }
        onSend(outgoingText);
      }
      markSending();
      if (textareaRef.current) textareaRef.current.value = "";
      setText("");
      setIsExpanded(false);
    }
    setIsInputEmpty((textareaRef.current?.value ?? "").length < 10);
  };

  useEffect(() => {
    if (textareaRef?.current?.value) {
      setIsInputEmpty(textareaRef?.current?.value?.length < 10);
    }
  }, [textareaRef?.current?.value]);

  const handleSend = async () => {
    if (textareaRef.current.value?.length > 30000) {
      return AppToasty.show(t("toasts.characters.maxLimit"), {
        type: "danger",
      });
    }
    // Mixed-mode: text turns are rejected while a voice segment is live
    // (backend 409 SESSION_MODE_LOCKED) — stop voice first.
    if (isLiveKitMode) {
      return AppToasty.show(
        t("chats.placeholders.stopLiveMode") +
          " — " +
          (t("toasts.liveMode.textBlocked") ||
            "stop the voice session to send text"),
        { type: "warning", duration: 3000 },
      );
    }
    if (
      (!isStreaming ||
        isPlannerAcceptingFeedback ||
        isAgenticRAGAcceptingFeedback) &&
      textareaRef.current.value?.trim()?.length > 0
    ) {
      // Delegation is text-driven: the "@Agent Label" mention pill picks the
      // agent (normal mode) and is stripped from the sent text; home mode
      // keeps mentions in the text for downstream multi-agent routing
      const { text: outgoingText, agent: agentForSend } = deriveAgentRouting(
        textareaRef.current.value,
      );
      // If an agent was mentioned, use bundled send to avoid race conditions
      if (agentForSend && onSendWithAgent) {
        onSendWithAgent(agentForSend, outgoingText);
      } else {
        if (agentForSend) {
          await onAgentSelected(agentForSend);
        }
        onSend(outgoingText);
      }
      markSending();
      textareaRef.current.value = null;
      setText("");
      setIsExpanded(false);
      textareaRef.current?.focus();
    }
  };
  return (
    <>
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-10px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.8;
            }
          }
          @media (max-width: 768px) {
            .chat-input-hints {
              display: none !important;
            }
            .chat-input-shift-enter {
              display: none !important;
            }
          }
        `}
      </style>

      {/* LiveKit Voice Agent Confirmation Dialog */}
      {showLiveKitConfirmation && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backgroundColor: "rgba(0,0,0,0.4)",
          }}
          onClick={() => setShowLiveKitConfirmation(false)}
        >
          <div
            style={{
              backgroundColor: currentTheme?.theme?.lightGrey,
              padding: isMobileScreen() ? "16px" : "24px",
              borderRadius: "12px",
              width: "calc(100vw - 32px)",
              maxWidth: "400px",
              maxHeight: "calc(100vh - 48px)",
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              animation: "slideIn 0.2s ease-out",
              border: `1px solid ${currentTheme?.theme?.border_color}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: "16px" }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: 12,
                  gap: 10,
                }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.liveMode}
                  size={22}
                  color={currentTheme?.theme?.primary}
                />
                <AppText
                         style={{
                           fontSize: currentTheme?.theme?.small_font_size,
                           fontWeight: "600",
                           ...(isMobileScreen() && {
                             whiteSpace: "nowrap",
                             overflow: "hidden",
                             textOverflow: "ellipsis",
                           }),
                         }}
                         color="#fff"
                       >
                  {t("chats.livekit.modal.title")}
                </AppText>
              </View>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.small_font_size,
                  lineHeight: 18.5,
                }}
                color={currentTheme?.theme?.darkGrey}
              >
                {t("chats.livekit.modal.description")}
              </AppText>
            </div>
            {/* Microphone Selection */}
            <div style={{ marginBottom: "16px" }}>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.small_font_size,
                  fontWeight: "500",
                  marginBottom: 8,
                }}
                color={currentTheme?.theme?.font_color}
              >
                🎤 {t("chats.livekit.modal.microphoneLabel")}
              </AppText>
              {isLoadingDevices ? (
                <AppText
                  style={{ fontSize: currentTheme?.theme?.small_font_size }}
                  color={currentTheme?.theme?.darkGrey}
                >
                  {t("chats.livekit.modal.loadingDevices")}
                </AppText>
              ) : availableAudioDevices.inputs.length === 0 ? (
                <AppText
                  style={{ fontSize: currentTheme?.theme?.small_font_size }}
                  color={currentTheme?.theme?.red}
                >
                  {t("chats.livekit.modal.noMicrophones")}
                </AppText>
              ) : (
                <select
                  value={selectedAudioInputDevice || ""}
                  onChange={(e) => setSelectedAudioInputDevice(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: `1px solid ${currentTheme?.theme?.border_color}`,
                    backgroundColor:
                      currentTheme?.theme?.inputBackground ||
                      currentTheme?.theme?.white,
                    color: "#000000",
                    fontSize: currentTheme?.theme?.small_font_size,
                    fontFamily: "normalFont",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {availableAudioDevices.inputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label ||
                        t("chats.livekit.modal.microphoneFallback", {
                          deviceId: device.deviceId.slice(0, 8),
                        })}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {/* Speaker Selection */}
            <div style={{ marginBottom: "16px" }}>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.small_font_size,
                  fontWeight: "500",
                  marginBottom: 8,
                }}
                color={currentTheme?.theme?.font_color}
              >
                🔊 {t("chats.livekit.modal.speakerLabel")}
              </AppText>
              {isLoadingDevices ? (
                <AppText
                  style={{ fontSize: currentTheme?.theme?.small_font_size }}
                  color={currentTheme?.theme?.darkGrey}
                >
                  {t("chats.livekit.modal.loadingDevices")}
                </AppText>
              ) : availableAudioDevices.outputs.length === 0 ? (
                <AppText
                  style={{ fontSize: currentTheme?.theme?.small_font_size }}
                  color={currentTheme?.theme?.darkGrey}
                >
                  {t("chats.livekit.modal.noSpeakers")}
                </AppText>
              ) : (
                <select
                  value={selectedAudioOutputDevice || ""}
                  onChange={(e) => setSelectedAudioOutputDevice(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: `1px solid ${currentTheme?.theme?.border_color}`,
                    backgroundColor:
                      currentTheme?.theme?.inputBackground ||
                      currentTheme?.theme?.white,
                    color: "#000000",
                    fontSize: currentTheme?.theme?.small_font_size,
                    fontFamily: "normalFont",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {availableAudioDevices.outputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label ||
                        t("chats.livekit.modal.speakerFallback", {
                          deviceId: device.deviceId.slice(0, 8),
                        })}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
                flexDirection: isMobileScreen() ? "column-reverse" : "row",
              }}
            >
              <button
                onClick={() => setShowLiveKitConfirmation(false)}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: `1px solid ${currentTheme?.theme?.border_color}`,
                  backgroundColor: "transparent",
                  color: currentTheme?.theme?.darkGrey,
                  fontSize: currentTheme?.theme?.small_font_size,
                  cursor: "pointer",
                  fontFamily: "normalFont",
                  transition: "all 0.2s",
                  width: isMobileScreen() ? "100%" : "auto",
                }}
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={startLiveKitMode}
                disabled={
                  availableAudioDevices.inputs.length === 0 || isLoadingDevices
                }
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor:
                    availableAudioDevices.inputs.length === 0 ||
                    isLoadingDevices
                      ? currentTheme?.theme?.darkGrey
                      : currentTheme?.theme?.primary,
                  color: "#fff",
                  fontSize: currentTheme?.theme?.small_font_size,
                  fontWeight: "600",
                  cursor:
                    availableAudioDevices.inputs.length === 0 ||
                    isLoadingDevices
                      ? "not-allowed"
                      : "pointer",
                  fontFamily: "normalFont",
                  transition: "all 0.2s",
                  opacity:
                    availableAudioDevices.inputs.length === 0 ||
                    isLoadingDevices
                      ? 0.6
                      : 1,
                  width: isMobileScreen() ? "100%" : "auto",
                }}
              >
                {t("chats.livekit.modal.startButton")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          width: "100%",
          position: "relative",
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <View
          style={{
            width: "100%",
            flexDirection: "row",
            alignItems: "flex-end",
            justifyContent: "space-between",
          }}
        >
          {!(showWidgetChatsHistory || canInteractWithChat === false) &&
            agentMode !== "voice" && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: _prompts.length > 0 && 0,
                  marginLeft: 5,
                  gap: 5,
                  width: displayQuickQuestions ? "30%" : "100%",
                  justifyContent: "space-between",
                }}
              >
                <View
                  style={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                    maxWidth: displayQuickQuestions ? "100%" : "100%",
                    flexShrink: 1,
                    gap: 6,
                  }}
                >
                  {videoUrl && (
                    <ChatMediaLeaf
                      onUploaded={onVideoUploaded}
                      url={videoUrl}
                      type="video"
                      fileName={detectFileName(videoUrl)}
                      fileExtension={detectExtensionFromFileName(videoUrl)}
                    />
                  )}

                  {fileUrls &&
                    fileUrls
                      .map((fileUrl, index) => ({ fileUrl, index }))
                      .filter(({ fileUrl }) => isCaptureChipVisible(fileUrl))
                      .map(({ fileUrl, index }) => {
                      return (
                        <ChatMediaLeaf
                          key={`file-${index}`}
                          onUploaded={() =>
                            onFilesUploaded(null, null, index, "file")
                          }
                          url={fileUrl}
                          type="file"
                          fileName={detectFileName(fileUrl)}
                          fileExtension={detectExtensionFromFileName(fileUrl)}
                        />
                      );
                    })}
                  {imageUrls &&
                    imageUrls
                      .map((imageUrl, index) => ({ imageUrl, index }))
                      .filter(({ imageUrl }) => isCaptureChipVisible(imageUrl))
                      .map(({ imageUrl, index }) => {
                      return (
                        <ChatMediaLeaf
                          key={`image-${index}`}
                          onUploaded={() =>
                            onFilesUploaded(null, null, index, "image")
                          }
                          url={imageUrl}
                          type="image"
                          fileName={detectFileName(imageUrl)}
                          fileExtension={detectExtensionFromFileName(imageUrl)}
                        />
                      );
                    })}
                  {audioUrls &&
                    audioUrls.map((audioUrl, index) => {
                      return (
                        <ChatMediaLeaf
                          key={`audio-${index}`}
                          onUploaded={() =>
                            onFilesUploaded(null, null, index, "audio")
                          }
                          url={audioUrl}
                          type="audio"
                          fileName={detectFileName(audioUrl)}
                          fileExtension={detectExtensionFromFileName(audioUrl)}
                        />
                      );
                    })}
                  {recordingUploadInProgress && (
                    <AppText
                      color={currentTheme?.theme?.orange}
                      style={{
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                      }}
                      preventTranslation
                    >
                      {t("chats.placeholders.transcriptionInProgress")}{" "}
                      <strong>{recordingUploadProgress}%</strong>
                    </AppText>
                  )}
                  {fileUploadInProgress && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        minWidth: 160,
                        marginBottom: 7,
                      }}
                    >
                      <AppText
                        color={currentTheme?.theme?.orange}
                        style={{
                          fontSize: currentTheme?.theme?.extra_small_font_size,
                        }}
                        wrapperStyle={{
                          marginRight: 5,
                        }}
                        preventTranslation
                      >
                        {fileUploadStatus === "converting" ? (
                          t("chats.placeholders.preparingFiles")
                        ) : (
                          <>
                            {t(
                              "chats.placeholders.uploadingFilesWithProgress",
                              { progress: fileUploadProgress },
                            )}
                          </>
                        )}
                      </AppText>
                    </View>
                  )}
                </View>
                {/* Approve Plan Button - aligned to right */}
                {planStatus === "ready" && planMode && (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={t("chats.ui.approvePlan")}
                    action="hover"
                    position="top"
                  >
                    <div
                      onClick={async () => {
                        await onPlanModeToggle(false);
                        onSend("Plan approved");
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: "4px 16px",
                        borderRadius: "8px",
                        border: "1px solid",
                        backgroundColor: currentTheme?.theme?.primary,
                        transition: "all 0.2s ease",
                        gap: "6px",
                        marginRight: 10,
                      }}
                    >
                      <span
                        style={{
                          fontSize: currentTheme?.theme?.small_font_size,
                          fontWeight: "600",
                          color: currentTheme?.theme?.white,
                          fontFamily: "normalFont",
                        }}
                      >
                        {t("chats.ui.approvePlanButton")}
                      </span>
                    </div>
                  </Popable>
                )}
              </View>
            )}
        </View>
        {/* Voice Mode Only Interface */}
        {agentMode === "voice" &&
          !showWidgetChatsHistory &&
          canInteractWithChat &&
          isLiveModeAvailableInRegion() && (
            <View
              style={{
                width: "100%",
                flexDirection: "row",
                flexWrap: isMobileScreen() ? "wrap" : "nowrap",
                paddingTop: 15,
                paddingBottom: 15,
                paddingLeft: isChatEmpty && !isMobileScreen() ? 40 : 12,
                paddingRight: isMobileScreen() ? 12 : 20,
                backgroundColor: currentTheme?.theme?.lightGrey,
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 3,
                marginLeft: 0,
                borderRadius: 10,
                marginTop: 2,
                gap: isMobileScreen() ? 8 : 12,
              }}
            >
              {!isLiveKitMode ? (
                // Live mode is not available in EU/US regions — the entry
                // point is hidden entirely there (text chat still works).
                isLiveModeAvailableInRegion() && (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                    style={[{ top: 20, zIndex: 20, maxWidth: 300 }]}
                    content={t("chats.ui.startLiveMode")}
                    action="hover"
                    position="top"
                  >
                    <div
                      onClick={openLiveKitConfirmation}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: "12px 24px",
                        borderRadius: "10px",
                        backgroundColor: currentTheme?.theme?.primary,
                        transition: "all 0.2s ease",
                        gap: "10px",
                        border: `1px solid ${currentTheme?.theme?.primary}`,
                      }}
                    >
                      <AppIcon
                        icon={AppAssets?.icons?.liveMode}
                        size={18}
                        color="#fff"
                      />
                      <AppText
                        style={{
                          fontSize: currentTheme?.theme?.small_font_size,
                          fontWeight: "600",
                        }}
                        color="#fff"
                      >
                        {t("chats.livekit.modal.startButton")}
                      </AppText>
                    </div>
                  </Popable>
                )
              ) : (
                <>
                  {/* Main Status Button */}
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[{ top: 20, zIndex: 20, maxWidth: 300 }]}
                    content={t("chats.ui.stopLiveMode")}
                    action="hover"
                    position="top"
                  >
                    <div
                      onClick={stopLiveKitMode}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: "12px 20px",
                        borderRadius: "10px",
                        backgroundColor:
                          liveKitAgentState === LIVEKIT_AGENT_STATES?.SPEAKING
                            ? currentTheme?.theme?.green
                            : liveKitAgentState ===
                              LIVEKIT_AGENT_STATES?.LISTENING
                            ? currentTheme?.theme?.orange
                            : liveKitAgentState ===
                              LIVEKIT_AGENT_STATES?.THINKING
                            ? currentTheme?.theme?.orange
                            : currentTheme?.theme?.darkGrey,
                        transition: "all 0.2s ease",
                        gap: "10px",
                        minWidth: isMobileScreen() ? 0 : 160,
                        flexShrink: 1,
                      }}
                    >
                      <View
                        style={{
                          animation:
                            liveKitAgentState ===
                              LIVEKIT_AGENT_STATES?.LISTENING ||
                            liveKitAgentState === LIVEKIT_AGENT_STATES?.SPEAKING
                              ? "pulse 1s ease-in-out infinite"
                              : "none",
                        }}
                      >
                        <AppIcon
                          icon={
                            liveKitAgentState === LIVEKIT_AGENT_STATES?.SPEAKING
                              ? AppAssets?.icons?.speakers
                              : liveKitAgentState ===
                                LIVEKIT_AGENT_STATES?.LISTENING
                              ? AppAssets?.icons?.liveMode
                              : liveKitAgentState ===
                                LIVEKIT_AGENT_STATES?.THINKING
                              ? AppAssets?.icons?.reasoning
                              : AppAssets?.icons?.stopGeneration
                          }
                          size={18}
                          color="#fff"
                        />
                      </View>
                      <AppText
                        style={{
                          fontSize: currentTheme?.theme?.small_font_size,
                          fontWeight: "600",
                        }}
                        color="#fff"
                      >
                        {liveKitAgentState === LIVEKIT_AGENT_STATES?.SPEAKING
                          ? t("chats.placeholders.speaking")
                          : liveKitAgentState ===
                            LIVEKIT_AGENT_STATES?.LISTENING
                          ? t("chats.placeholders.stopLiveMode")
                          : liveKitAgentState === LIVEKIT_AGENT_STATES?.THINKING
                          ? t("chats.placeholders.thinking")
                          : liveKitAgentState ===
                            LIVEKIT_AGENT_STATES?.INITIALIZING
                          ? t("chats.placeholders.starting")
                          : liveKitConnectionState ===
                            LIVEKIT_CONNECTION_STATES?.CONNECTING
                          ? t("chats.placeholders.connecting")
                          : t("chats.placeholders.stopLiveMode")}
                      </AppText>
                    </div>
                  </Popable>

                  {/* Mute/Unmute Button */}
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[{ top: 20, zIndex: 20, maxWidth: 300 }]}
                    content={
                      isLiveKitMicEnabled
                        ? t("chats.placeholders.muteMicrophone")
                        : t("chats.placeholders.unmuteMicrophone")
                    }
                    action="hover"
                    position="top"
                  >
                    <div
                      onClick={toggleLiveKitMicrophone}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: "12px 16px",
                        borderRadius: "10px",
                        backgroundColor: isLiveKitMicEnabled
                          ? currentTheme?.theme?.lightGrey
                          : currentTheme?.theme?.red,
                        border: `1px solid ${
                          isLiveKitMicEnabled
                            ? currentTheme?.theme?.border_color
                            : currentTheme?.theme?.red
                        }`,
                        transition: "all 0.2s ease",
                        gap: "8px",
                      }}
                    >
                      <AppIcon
                        icon={
                          isLiveKitMicEnabled
                            ? AppAssets?.icons?.microphone
                            : AppAssets?.icons?.microphoneSlash
                        }
                        size={16}
                        color={
                          isLiveKitMicEnabled
                            ? currentTheme?.theme?.primary
                            : "#fff"
                        }
                      />
                      <AppText
                        style={{
                          fontSize: currentTheme?.theme?.small_font_size,
                          fontWeight: "500",
                        }}
                        color={
                          isLiveKitMicEnabled
                            ? currentTheme?.theme?.font_color
                            : "#fff"
                        }
                      >
                        {isLiveKitMicEnabled
                          ? t("chats.placeholders.mute")
                          : t("chats.placeholders.unmute")}
                      </AppText>
                    </div>
                  </Popable>

                  {/* Debug Panel Toggle */}
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[{ top: 20, zIndex: 20, maxWidth: 300 }]}
                    content={
                      showDebugPanel
                        ? t("chats.placeholders.hideDebugPanel")
                        : t("chats.placeholders.showDebugPanel")
                    }
                    action="hover"
                    position="top"
                  >
                    <div
                      onClick={() => setShowDebugPanel(!showDebugPanel)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: "12px",
                        borderRadius: "10px",
                        backgroundColor: showDebugPanel
                          ? currentTheme?.theme?.primary_soft
                          : currentTheme?.theme?.lightGrey,
                        border: `1px solid ${
                          showDebugPanel
                            ? currentTheme?.theme?.primary
                            : currentTheme?.theme?.border_color
                        }`,
                        transition: "all 0.2s ease",
                      }}
                    >
                      <AppIcon
                        icon={AppAssets?.icons?.terminal}
                        size={16}
                        color={
                          showDebugPanel
                            ? currentTheme?.theme?.primary
                            : currentTheme?.theme?.darkGrey
                        }
                      />
                    </div>
                  </Popable>
                </>
              )}
            </View>
          )}

        {/* Standard Text Input Interface (hidden in voice mode) */}
        {agentMode !== "voice" && (
          <View
            style={{
              width: "100%",
              flexDirection: "row",
              paddingTop: 10,
              paddingBottom: 10,
              paddingLeft: 10,
              paddingRight: isChatEmpty ? 10 : 20,
              backgroundColor: isDragging
                ? currentTheme?.theme?.primary_soft
                : currentTheme?.theme?.lightGrey,
              justifyContent: "space-between",
              maxHeight: isExpanded ? 400 : 140,
              marginBottom: 10,
              height: isExpanded ? 400 : 140,
              marginLeft: !isChatEmpty && 10,
              borderRadius: 10,
              marginTop: 2,
              borderWidth: isDragging
                ? 2
                : isAwaitingUserInput || handedOffToHuman
                ? 1
                : isExpanded
                ? 1
                : 0,
              borderColor:
                isAwaitingUserInput || handedOffToHuman
                  ? currentTheme?.theme?.primary
                  : isDragging
                  ? currentTheme?.theme?.primary
                  : isExpanded
                  ? currentTheme?.theme?.border_color
                  : currentTheme?.theme?.transparent,
              borderStyle: isDragging ? "dashed" : "solid",
              transition: "all 0.2s ease",
              ...(isExpanded &&
                !isChatEmpty && {
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 10,
                  zIndex: 1000,
                  boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
                }),
              ...(isExpanded &&
                isChatEmpty && {
                  position: !isChatEmpty && "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
                }),
            }}
          >
            <View
              style={{ position: "relative", flex: 1, flexDirection: "column" }}
            >
              {isDragging && (
                <View
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: currentTheme?.theme?.primary_soft,
                    borderRadius: 5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 999,
                    pointerEvents: "none",
                  }}
                >
                  <AppText
                    style={{
                      fontSize: currentTheme?.theme?.medium_font_size,
                      fontWeight: "bold",
                    }}
                    color={currentTheme?.theme?.primary}
                  >
                    Drop files here
                  </AppText>
                  <AppText
                    style={{
                      fontSize: currentTheme?.theme?.small_font_size,
                      marginTop: 5,
                    }}
                    color={currentTheme?.theme?.darkGrey}
                  >
                    Images, videos, audios, and documents
                  </AppText>
                </View>
              )}
              {/* Structured Questions Input or Regular Textarea */}
              {isAwaitingUserInput && structuredQuestions ? (
                <StructuredQuestionsInput
                  structuredQuestions={structuredQuestions}
                  onSend={onSend}
                  maxHeight={isExpanded ? 360 : 110}
                  currentTheme={currentTheme}
                  setIsExpanded={setIsExpanded}
                />
              ) : ENABLE_CHIP_COMPOSER ? (
                <TokenComposerInput
                  ref={textareaRef}
                  defaultValue={text}
                  placeholder={getPlaceholderText()}
                  currentTheme={currentTheme}
                  opacity={isDragging ? 0.3 : 1}
                  disabled={
                    ((showWidgetChatsHistory ||
                      canInteractWithChat === false) &&
                      handedOffToHuman != true) ||
                    audioProcessingInProgress ||
                    isSpeechGettingProcessed
                  }
                  onChange={handleComposerInput}
                  onKeyDown={handleKeyDown}
                  onPaste={handleComposerPaste}
                  labelMap={composerLabelMap}
                  agentLabels={agentLabels}
                  style={{
                    width: `calc(100% - ${
                      allowFolderBinding ? "75px" : "55px"
                    } )`,
                  }}
                />
              ) : (
                <textarea
                  ref={textareaRef}
                  defaultValue={text}
                  placeholder={getPlaceholderText()}
                  style={{
                    fontFamily: "normalFont",
                    fontSize: currentTheme?.theme?.small_font_size,
                    color: currentTheme?.theme?.font_color,
                    backgroundColor: currentTheme?.theme?.transparent,
                    flex: 1,
                    width: `calc(100% - ${
                      allowFolderBinding ? "75px" : "55px"
                    } )`,
                    opacity: isDragging ? 0.3 : 1,
                  }}
                  disabled={
                    ((showWidgetChatsHistory ||
                      canInteractWithChat === false) &&
                      handedOffToHuman != true) ||
                    audioProcessingInProgress ||
                    isSpeechGettingProcessed
                  }
                  onKeyDown={handleKeyDown}
                  onInput={handleTextareaInput}
                />
              )}
              {showMentionDropdown && filteredPrompts.length > 0 && (
                <View
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: currentTheme?.theme?.lightGrey,
                    borderRadius: 5,
                    maxHeight: 200,
                    overflowY: "auto",
                    zIndex: 1000,
                    borderWidth: 0.5,
                    borderColor: currentTheme?.theme?.border_color,
                  }}
                >
                  {filteredPrompts.map((prompt, index) => (
                    <AppText
                      key={prompt.id}
                      onPress={() => handleMentionSelect(prompt)}
                      isClickable
                      numberOfLines={1}
                      style={{
                        padding: 6,
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                        borderBottomWidth:
                          index < filteredPrompts.length - 1 ? 0.5 : 0,
                        borderBottomColor: currentTheme?.theme?.mediumGrey,
                      }}
                      wrapperStyle={{
                        marginHorizontal: 5,
                        marginVertical: 1,
                        borderRadius: 3,
                      }}
                      color={currentTheme?.theme?.darkGrey}
                    >
                      {prompt?.interpolationString}
                    </AppText>
                  ))}
                </View>
              )}
              {showToolDropdown && filteredTools.length > 0 && (
                <View
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: currentTheme?.theme?.lightGrey,
                    borderRadius: 8,
                    maxHeight: 300,
                    overflowY: "auto",
                    zIndex: 1000,
                    borderWidth: 0.5,
                    borderColor: currentTheme?.theme?.border_color,
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 4,
                  }}
                >
                  {filteredTools.map((tool, index) => (
                    <div
                      key={tool.id + index}
                      onClick={() => handleToolSelect(tool)}
                      style={{
                        padding: 6,
                        borderBottomWidth:
                          index < filteredTools.length - 1 ? 0.5 : 0,
                        borderBottomColor: currentTheme?.theme?.mediumGrey,
                        cursor: "pointer",
                        borderBottomStyle: "solid",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          currentTheme?.theme?.primary_soft;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "flex-start",
                          gap: 6,
                        }}
                      >
                        <View style={{ flex: 1 }}>
                          <AppText
                            style={{
                              fontSize:
                                currentTheme?.theme?.extra_small_font_size,
                              fontWeight: "bold",
                              marginBottom: 1,
                            }}
                            color={currentTheme?.theme?.primary}
                          >
                            {tool?.name}
                          </AppText>
                          <AppText
                            style={{
                              fontSize: 10,
                              opacity: 0.7,
                            }}
                            color={currentTheme?.theme?.darkGrey}
                            numberOfLines={2}
                          >
                            {tool?.description}
                          </AppText>
                        </View>
                      </View>
                    </div>
                  ))}
                </View>
              )}
              {showAgentDropdown && filteredAgents.length > 0 && (
                <View
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: currentTheme?.theme?.lightGrey,
                    borderRadius: 8,
                    maxHeight: 300,
                    overflowY: "auto",
                    zIndex: 1000,
                    borderWidth: 0.5,
                    borderColor: currentTheme?.theme?.border_color,
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 4,
                  }}
                >
                  {filteredAgents.map((agent, index) => (
                    <div
                      key={agent.id + index}
                      onClick={() => handleAgentSelect(agent)}
                      style={{
                        padding: 6,
                        borderBottomWidth:
                          index < filteredAgents.length - 1 ? 0.5 : 0,
                        borderBottomColor: currentTheme?.theme?.mediumGrey,
                        cursor: "pointer",
                        borderBottomStyle: "solid",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          currentTheme?.theme?.primary_soft;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "flex-start",
                          gap: 6,
                        }}
                      >
                        <View style={{ flex: 1 }}>
                          <AppText
                            style={{
                              fontSize:
                                currentTheme?.theme?.extra_small_font_size,
                              fontWeight: "bold",
                              marginBottom: 1,
                            }}
                            color={currentTheme?.theme?.orange}
                          >
                            {agent?.label}
                          </AppText>
                          <AppText
                            style={{
                              fontSize: 10,
                              opacity: 0.7,
                            }}
                            color={currentTheme?.theme?.darkGrey}
                            numberOfLines={2}
                          >
                            {agent?.description || "No description available"}
                          </AppText>
                        </View>
                      </View>
                    </div>
                  ))}
                </View>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  paddingTop: 4,
                  marginTop: "auto",
                }}
              >
                <span
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    color: currentTheme?.theme?.darkGrey,
                    fontFamily: "normalFont",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {/* Expand/Collapse Button */}
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 200,
                      },
                    ]}
                    content={
                      isExpanded
                        ? t("chats.ui.collapseInput")
                        : t("chats.ui.expandInput")
                    }
                    action="hover"
                    position="top"
                  >
                    <div
                      onClick={() => setIsExpanded(!isExpanded)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        backgroundColor: isExpanded
                          ? currentTheme?.theme?.primary_soft
                          : "transparent",
                        transition: "all 0.2s ease",
                        marginRight: 4,
                      }}
                    >
                      <AppIcon
                        icon={
                          isExpanded
                            ? AppAssets?.icons?.collapse
                            : AppAssets?.icons?.expand
                        }
                        size={12}
                        color={
                          isExpanded
                            ? currentTheme?.theme?.primary
                            : currentTheme?.theme?.darkGrey
                        }
                        wrapperStyle={{
                          margin: 0,
                        }}
                      />
                    </div>
                  </Popable>
                  {(effectiveAgenticRAG ||
                    effectiveUseInterleavedReasoning) && (
                    <AppText
                      onPress={() =>
                        !isAwaitingUserInput && onPlanModeToggle(!planMode)
                      }
                      isClickable={!isAwaitingUserInput}
                      isHoverable={false}
                      color={
                        planMode
                          ? currentTheme?.theme?.primary
                          : currentTheme?.theme?.mediumGrey
                      }
                      style={{
                        fontWeight: planMode ? "bold" : "normal",
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                        opacity: isAwaitingUserInput ? 0.5 : 1,
                      }}
                      rightIcon={
                        planMode
                          ? AppAssets?.icons?.planMode
                          : AppAssets?.icons?.executeMode
                      }
                      iconSize={12}
                    >
                      {`${planMode ? "Plan" : "Task"} mode`}
                    </AppText>
                  )}
                  {(effectiveAgenticRAG || effectiveUseInterleavedReasoning) &&
                    planFile && (
                      <>
                        {" | "}

                        <AppText
                          onPress={onShowPlan}
                          isClickable
                          isHoverable={false}
                          color={currentTheme?.theme?.orange}
                          style={{
                            fontSize:
                              currentTheme?.theme?.extra_small_font_size,
                            fontWeight: "bold",
                          }}
                          rightIcon={AppAssets?.icons?.list}
                          iconSize={12}
                        >
                          {t("chats.ui.showPlan")}
                        </AppText>
                      </>
                    )}

                  <span className="chat-input-hints">
                    {!isChatEmpty && (
                      <>
                        {(effectiveAgenticRAG ||
                          effectiveUseInterleavedReasoning) &&
                          ` ${t("common.separator")} `}
                        {agentMode === "planner"
                          ? `@ ${t("chats.ui.promptsLabel")} (${
                              _prompts.length
                            })`
                          : `@ ${t("chats.ui.promptsLabel")} (${
                              _prompts.length
                            }) ${t("common.separator")} ~ ${t(
                              "chats.ui.toolsLabel",
                            )} (${filteredTools.length}) ${t(
                              "common.separator",
                            )} ${
                              isHomeMode
                                ? t("chats.ui.delegationHint")
                                : `# ${t("chats.ui.agentsLabel")} (${
                                    filteredAgents.length
                                  })`
                            }`}
                      </>
                    )}
                  </span>
                </span>
                {isChatEmpty && (
                  <span
                    style={{
                      fontSize: currentTheme?.theme?.extra_small_font_size,
                      color: currentTheme?.theme?.darkGrey,
                      fontFamily: "normalFont",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {(effectiveAgenticRAG ||
                      effectiveUseInterleavedReasoning) && (
                      <span
                        onClick={() =>
                          !isAwaitingUserInput &&
                          !forcePlanMode &&
                          onPlanModeToggle(!planMode)
                        }
                        style={{
                          cursor:
                            isAwaitingUserInput || forcePlanMode
                              ? "not-allowed"
                              : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          opacity:
                            isAwaitingUserInput || forcePlanMode ? 0.5 : 1,
                          fontWeight: "bold",
                        }}
                      >
                        {planMode
                          ? t("chats.ui.planMode")
                          : t("chats.ui.taskMode")}
                      </span>
                    )}
                    {(effectiveAgenticRAG ||
                      effectiveUseInterleavedReasoning) &&
                      showUploadOption &&
                      " | "}
                    {showUploadOption && (
                      <span
                        onClick={() => fileInputRef.current.click()}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          fontWeight: "bold",
                          color: currentTheme?.theme?.primary,
                          gap: 4,
                        }}
                      >
                        {t("chats.ui.upload")}
                      </span>
                    )}
                    {showUploadOption && agenticTooling && !documentId && " | "}
                    {agenticTooling && !documentId && (
                      <span
                        onClick={() => {
                          if (
                            !fileUploadInProgress &&
                            !audioProcessingInProgress &&
                            !isStreaming &&
                            !documentId
                          ) {
                            handleCanvasCreation();
                          }
                        }}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontWeight: "bold",
                          color: currentTheme?.theme?.primary,
                        }}
                      >
                        {t("chats.ui.canvas")}
                      </span>
                    )}
                    {!primaryAgentId?.startsWith("tf_main") &&
                    (((showUploadOption || (agenticTooling && !documentId)) &&
                      isUserContentAdmin() &&
                      isConnectedToCloud()) ||
                      ((effectiveAgenticRAG ||
                        effectiveUseInterleavedReasoning) &&
                        isUserContentAdmin() &&
                        isConnectedToCloud()))
                      ? " | "
                      : null}
                    {isUserContentAdmin() &&
                      isConnectedToCloud() &&
                      !primaryAgentId?.startsWith("tf_main") && (
                        <span
                          onClick={() => {
                            if (
                              !fileUploadInProgress &&
                              !audioProcessingInProgress
                            ) {
                              handleGenerationOfCustomPrompt(
                                textareaRef.current.value,
                                [primaryAgentId],
                                false,
                              );
                            }
                          }}
                          style={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontWeight: "bold",
                            color: currentTheme?.theme?.primary,
                          }}
                        >
                          {t("chats.ui.savePrompt")}
                        </span>
                      )}
                  </span>
                )}
              </div>
            </View>

            {/* Icons Container - Grid layout for consistent spacing */}
            <div
              style={{
                display: "grid",
                gridAutoFlow: "column",
                gap: 5,
                alignItems: "center",
                height: "fit-content",
                position: isChatEmpty && "absolute",
                right: isChatEmpty && 10,
              }}
            >
              {allowFolderBinding &&
                !showWidgetChatsHistory &&
                canInteractWithChat &&
                !isLiveKitMode && (
                  <div
                    style={{ position: "relative" }}
                    data-folder-dropdown="true"
                  >
                    <Popable
                      numberOfLines={3}
                      backgroundColor={
                        currentTheme?.theme?.tooltipBackgroundColor
                      }
                      style={[
                        {
                          top: 20,
                          zIndex: 20,
                          maxWidth: 300,
                        },
                      ]}
                      content={
                        boundFolderPath
                          ? `${t("chats.ui.boundTo")}: ${boundFolderName}`
                          : t("chats.ui.bindFolderForAccess")
                      }
                      action="hover"
                      position="left"
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <AppIcon
                          icon={AppAssets?.icons?.folder}
                          color={
                            boundFolderPath
                              ? currentTheme?.theme?.green
                              : currentTheme?.theme?.darkGrey
                          }
                          size={16}
                          wrapperStyle={{
                            margin: 0,
                          }}
                          isClickable={
                            !fileUploadInProgress &&
                            !audioProcessingInProgress &&
                            !isFolderSelectionOpen
                          }
                          onPress={
                            boundFolderPath
                              ? handleUnbindFolder
                              : () => setShowFolderDropdown(!showFolderDropdown)
                          }
                        />
                      </div>
                    </Popable>
                    {showFolderDropdown && !boundFolderPath && (
                      <View
                        style={{
                          position: "absolute",
                          bottom: "100%",
                          right: 0,
                          backgroundColor: currentTheme?.theme?.lightGrey,
                          borderRadius: 8,
                          minWidth: 200,
                          maxWidth: 350,
                          overflowY: "auto",
                          zIndex: 1001,
                          borderWidth: 0.5,
                          borderColor: currentTheme?.theme?.border_color,
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: 0.15,
                          shadowRadius: 4,
                          marginBottom: 4,
                        }}
                        data-folder-dropdown="true"
                      >
                        {recentFolders && recentFolders.length > 0 && (
                          <>
                            {recentFolders
                              .slice(0, 10)
                              .map((folderPath, index) => {
                                if (!folderPath) return null;
                                const folderName =
                                  folderPath.split(/[\\/]/).pop() || folderPath;
                                return (
                                  <div
                                    key={folderPath + index}
                                    onClick={() =>
                                      handleSelectRecentFolder(folderPath)
                                    }
                                    style={{
                                      padding: "8px 12px",
                                      borderBottomWidth:
                                        index <
                                        Math.min(recentFolders.length, 10) - 1
                                          ? 0.5
                                          : 0,
                                      borderBottomColor:
                                        currentTheme?.theme?.mediumGrey,
                                      cursor: "pointer",
                                      borderBottomStyle: "solid",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        currentTheme?.theme?.primary_soft;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        "transparent";
                                    }}
                                    data-folder-dropdown="true"
                                  >
                                    <AppText
                                      style={{
                                        fontSize:
                                          currentTheme?.theme
                                            ?.extra_small_font_size,
                                        fontWeight: "500",
                                      }}
                                      color={currentTheme?.theme?.darkGrey}
                                      numberOfLines={1}
                                      preventTranslation
                                    >
                                      {folderName}
                                    </AppText>
                                    <AppText
                                      style={{
                                        fontSize: 10,
                                        opacity: 0.6,
                                        marginTop: 2,
                                      }}
                                      color={currentTheme?.theme?.mediumGrey}
                                      numberOfLines={1}
                                      preventTranslation
                                    >
                                      {folderPath}
                                    </AppText>
                                  </div>
                                );
                              })}
                            <div
                              style={{
                                height: 1,
                                backgroundColor:
                                  currentTheme?.theme?.mediumGrey,
                                margin: "4px 8px",
                              }}
                            />
                          </>
                        )}

                        <AppText
                          wrapperStyle={{
                            padding: 10,
                          }}
                          data-folder-dropdown="true"
                          isClickable
                          onPress={handleSelectFolder}
                          style={{
                            fontSize:
                              currentTheme?.theme?.extra_small_font_size,
                            fontWeight: "bold",
                          }}
                          color={currentTheme?.theme?.primary}
                        >
                          + Select new folder...
                        </AppText>
                      </View>
                    )}
                  </div>
                )}
              {/* Upload stays available during live mode — attachments
                  become chat context via /live/{id}/attachments and the
                  voice agent reads them with its read_attachment tool. */}
              {showUploadOption && !isChatEmpty && (
                <Popable
                  numberOfLines={2}
                  backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                  style={[
                    {
                      top: 20,
                      zIndex: 20,
                      maxWidth: 300,
                    },
                  ]}
                  content={t("ui.uploadFiles")}
                  action="hover"
                  position="bottom"
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AppIcon
                      isClickable
                      icon={AppAssets.icons.attachment}
                      size={16}
                      onPress={() => fileInputRef.current.click()}
                      color={currentTheme?.theme?.darkGrey}
                      wrapperStyle={{
                        margin: 0,
                      }}
                    />
                  </div>
                </Popable>
              )}

              {/* Live voice segment — gated on the voice-over capability
                  (allowAudiosGeneration): live voice is a voice feature.
                  Icon sits left of the mic; when a voice segment is live it
                  becomes the colored status indicator; click to stop.
                  Hidden entirely in EU/US where live mode is not
                  available. */}
              {allowAudiosGeneration &&
                !isStreaming &&
                !showWidgetChatsHistory &&
                agentMode !== "voice" &&
                liveSessionId &&
                canInteractWithChat &&
                !isLiveKitMode &&
                isLiveModeAvailableInRegion() && (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={t("chats.ui.startLiveMode")}
                    action="hover"
                    position="left"
                  >
                    <div
                      onClick={openLiveKitConfirmation}
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <AppIcon
                        icon={AppAssets?.icons?.liveMode}
                        size={16}
                        color={currentTheme?.theme?.darkGrey}
                      />
                    </div>
                  </Popable>
                )}

              {/* Live segment active — status/stop icon in place of the
                  hidden mic button */}
              {agentMode !== "voice" &&
                isLiveKitMode &&
                !showWidgetChatsHistory && (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={
                      liveKitAgentState === LIVEKIT_AGENT_STATES?.SPEAKING
                        ? t("chats.placeholders.speaking")
                        : liveKitAgentState === LIVEKIT_AGENT_STATES?.LISTENING
                          ? t("chats.placeholders.stopLiveMode")
                          : liveKitAgentState === LIVEKIT_AGENT_STATES?.THINKING
                            ? t("chats.placeholders.thinking")
                            : liveKitAgentState ===
                                LIVEKIT_AGENT_STATES?.INITIALIZING
                              ? t("chats.placeholders.starting")
                              : t("chats.placeholders.stopLiveMode")
                    }
                    action="hover"
                    position="left"
                  >
                    <div
                      onClick={stopLiveKitMode}
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <AppIcon
                        icon={AppAssets?.icons?.liveMode}
                        size={16}
                        color={
                          liveKitAgentState === LIVEKIT_AGENT_STATES?.SPEAKING
                            ? currentTheme?.theme?.green
                            : liveKitAgentState ===
                                LIVEKIT_AGENT_STATES?.LISTENING ||
                              liveKitAgentState ===
                                LIVEKIT_AGENT_STATES?.THINKING
                              ? currentTheme?.theme?.orange
                              : liveKitAgentState ===
                                  LIVEKIT_AGENT_STATES?.INITIALIZING
                                ? currentTheme?.theme?.orange
                                : currentTheme?.theme?.darkGrey
                        }
                      />
                    </div>
                  </Popable>
                )}

              {/* Voice wave — live equalizer feedback (ported from tfcode
                  voice mode): breathing ripple while listening, live mic
                  spectrum while the user speaks, dimmed while the agent
                  thinks. Renders next to the live status icon. */}
              {agentMode !== "voice" &&
                isLiveKitMode &&
                !showWidgetChatsHistory && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginLeft: 4,
                      pointerEvents: "none",
                    }}
                  >
                    <VoiceWave
                      state={
                        liveKitAgentState === LIVEKIT_AGENT_STATES?.SPEAKING
                          ? "speaking"
                          : liveKitAgentState ===
                              LIVEKIT_AGENT_STATES?.THINKING
                            ? "thinking"
                            : liveKitAgentState ===
                                LIVEKIT_AGENT_STATES?.LISTENING &&
                              isLiveKitAudioDetected
                              ? "user"
                              : // Warm-up phase: room connecting or agent
                                // still initializing — energizing sweep so
                                // the user sees progress before it's live
                                liveKitConnectionState ===
                                  LIVEKIT_CONNECTION_STATES?.CONNECTING ||
                                  liveKitAgentState ===
                                    LIVEKIT_AGENT_STATES?.INITIALIZING
                                ? "connecting"
                                : "listening"
                      }
                      bands={liveKitAudioSpectrum}
                      color={currentTheme?.theme?.primary}
                      accent={currentTheme?.theme?.green}
                    />
                  </div>
                )}

              {/* Mute/unmute self while a live voice segment is running —
                  prevents background noise from interrupting the agent. */}
              {agentMode !== "voice" &&
                isLiveKitMode &&
                !showWidgetChatsHistory && (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={
                      isLiveKitMicEnabled
                        ? t("chats.placeholders.muteMicrophone")
                        : t("chats.placeholders.unmuteMicrophone")
                    }
                    action="hover"
                    position="left"
                  >
                    <div
                      onClick={toggleLiveKitMicrophone}
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <AppIcon
                        icon={
                          isLiveKitMicEnabled
                            ? AppAssets?.icons?.microphone
                            : AppAssets?.icons?.microphoneSlash
                        }
                        size={16}
                        color={
                          isLiveKitMicEnabled
                            ? currentTheme?.theme?.primary
                            : currentTheme?.theme?.red
                        }
                      />
                    </div>
                  </Popable>
                )}

              {allowAudiosUpload &&
                !isStreaming &&
                !showWidgetChatsHistory &&
                !isLiveKitMode && (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={t("chats.ui.transcribe")}
                    action="hover"
                    position="left"
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AudioRecorder
                        onRecordingCompleted={handleRecordingCompleted}
                        onRecordingStarted={handleRecordingStarted}
                        style={{
                          justifyContent: "center",
                          marginTop: 0,
                          marginLeft: 0,
                          marginRight: 0,
                        }}
                        voiceSensitivity={voiceSensitivity}
                      />
                    </div>
                  </Popable>
                )}

              {!showWidgetChatsHistory &&
                canInteractWithChat &&
                !isLiveKitMode &&
                agenticTooling &&
                !isChatEmpty && (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={
                      !documentId
                        ? t("chats.placeholders.enableCanvas")
                        : isCanvasOpen
                        ? t("chats.placeholders.hideCanvas")
                        : t("chats.placeholders.showCanvas")
                    }
                    action="hover"
                    position="left"
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AppIcon
                        icon={AppAssets?.icons?.canvas}
                        color={
                          !documentId
                            ? currentTheme?.theme?.primary
                            : isCanvasOpen
                            ? currentTheme?.theme?.orange
                            : currentTheme?.theme?.orange
                        }
                        size={18}
                        wrapperStyle={{
                          margin: 0,
                        }}
                        isClickable={
                          !fileUploadInProgress &&
                          !audioProcessingInProgress &&
                          !isStreaming
                        }
                        onPress={() => {
                          if (!documentId) {
                            handleCanvasCreation();
                          } else {
                            onToggleCanvas();
                          }
                        }}
                      />
                    </div>
                  </Popable>
                )}

              {!showWidgetChatsHistory &&
                canInteractWithChat &&
                !isLiveKitMode &&
                isUserContentAdmin() &&
                isConnectedToCloud() &&
                !isChatEmpty &&
                !primaryAgentId?.startsWith("tf_main") && (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={t("chats.ui.savePrompt")}
                    action="hover"
                    position="left"
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AppIcon
                        icon={AppAssets?.icons?.bookmark}
                        color={currentTheme?.theme?.primary}
                        size={16}
                        wrapperStyle={{
                          margin: 0,
                        }}
                        isClickable={
                          !fileUploadInProgress && !audioProcessingInProgress
                        }
                        onPress={() => {
                          handleGenerationOfCustomPrompt(
                            textareaRef.current.value,
                            [primaryAgentId],
                            false,
                          );
                        }}
                      />
                    </div>
                  </Popable>
                )}

              {!showWidgetChatsHistory &&
                canInteractWithChat &&
                !isLiveKitMode &&
                (isSpeechGettingProcessed ? (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={t("chats.placeholders.generatingSpeech")}
                    action="hover"
                    position="left"
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AppIcon
                        icon={AppAssets?.icons?.speakers}
                        color={currentTheme?.theme?.orange}
                        size={18}
                        wrapperStyle={{
                          margin: 0,
                        }}
                        isClickable={false}
                      />
                    </div>
                  </Popable>
                ) : isPlaying ? (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={t("chats.ui.stopAudio")}
                    action="hover"
                    position="left"
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AppIcon
                        icon={AppAssets?.icons?.stopPlaying}
                        color={currentTheme?.theme?.red}
                        size={18}
                        wrapperStyle={{
                          margin: 0,
                        }}
                        isClickable={true}
                        onPress={() => {
                          stopAudio();
                        }}
                      />
                    </div>
                  </Popable>
                ) : (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 20,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={
                      isStreaming ? t("chats.ui.cancel") : t("chats.ui.send")
                    }
                    action="hover"
                    position="left"
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AppIcon
                        icon={
                          !isStreaming
                            ? AppAssets?.icons?.send
                            : AppAssets?.icons?.stopGeneration
                        }
                        color={isStreaming && currentTheme?.theme?.red}
                        size={18}
                        wrapperStyle={{
                          margin: 0,
                        }}
                        isClickable={
                          !fileUploadInProgress && !audioProcessingInProgress
                        }
                        onPress={
                          !isLastMessageEmpty && isStreaming
                            ? onCancel
                            : handleSend
                        }
                      />
                    </div>
                  </Popable>
                ))}
            </div>
            {/* Shift+Enter hint - inside the container so it's visible when expanded */}
            <span
              className="chat-input-shift-enter"
              style={{
                position: "absolute",
                bottom: 8,
                right: 20,
                fontSize: currentTheme?.theme?.extra_small_font_size,
                color: isExpanded
                  ? currentTheme?.theme?.primary
                  : currentTheme?.theme?.darkGrey,
                fontFamily: "normalFont",
                display:
                  (isExpanded || showTip) && !isChatEmpty ? "flex" : "none",
                alignItems: "center",
                gap: 4,
                fontWeight: isExpanded ? "600" : "normal",
              }}
            >
              {isExpanded && !isChatEmpty ? (
                <>
                  <span style={{ opacity: 0.8 }}>⇧</span>
                  Shift + Enter to send
                </>
              ) : (
                <>
                  <span style={{ opacity: 0.7 }}>{currentTip?.icon}</span>
                  {displayedTipText}
                </>
              )}
            </span>
          </View>
        )}
        {/* Tips displayed under input when chat is empty */}
        {isChatEmpty && agentMode !== "voice" && (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 8,
              marginTop: 10,
              maxWidth: "100%",
            }}
          >
            {[
              !primaryAgentId?.startsWith("tf_main") && {
                icon: "@",
                text: t("chats.ui.useSavedPrompts"),
              },
              { icon: "~", text: t("chats.ui.selectTools") },
              {
                icon: "#",
                text: isHomeMode
                  ? t("chats.ui.delegationHint")
                  : t("chats.ui.switchAgents"),
              },
              {
                icon: isExpanded ? "⇧" : "↵",
                text: isExpanded
                  ? t("chats.ui.shiftEnterToSend")
                  : t("chats.placeholders.shiftEnterNewLine"),
              },
            ]
              .filter(Boolean)
              .map((tip, index) => (
                <View
                  key={index}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor: currentTheme?.theme?.primary_soft,
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 20,
                    gap: 6,
                  }}
                >
                  <AppText
                    style={{
                      fontSize: currentTheme?.theme?.extra_small_font_size,
                      fontWeight: "600",
                    }}
                    color={currentTheme?.theme?.primary}
                    preventTranslation
                  >
                    {tip.icon}
                  </AppText>
                  <AppText
                    style={{
                      fontSize: currentTheme?.theme?.extra_small_font_size,
                    }}
                    color={currentTheme?.theme?.darkGrey}
                  >
                    {tip.text}
                  </AppText>
                </View>
              ))}
          </View>
        )}
        {displayQuickQuestions && agentMode !== "voice" && (
          <div
            ref={quickQuestionsContainerRef}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              flexWrap: "wrap",
              marginBottom: 10,
              gap: 2,
              paddingRight: 10,
              paddingLeft: 10,
              marginTop: 5,
            }}
          >
            {isChatEmpty && quickQuestions?.length > 0 && (
              <View
                style={{
                  alignItems: "flex-start",
                  marginBottom: 8,
                  width: "100%",
                }}
              >
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    opacity: 0.7,
                  }}
                  color={currentTheme?.theme?.darkGrey}
                >
                  {t("chats.ui.tryAsking")}
                </AppText>
              </View>
            )}
            <View
              style={{
                flexDirection: isChatEmpty ? "row" : "column",
                flexWrap: "wrap",
                justifyContent: isChatEmpty ? "flex-start" : "flex-end",
                gap: isChatEmpty ? 8 : 10,
                width: "100%",
              }}
            >
              {quickQuestions?.map((question, index) =>
                isChatEmpty ? (
                  <div
                    key={index}
                    onClick={() => onSend(question)}
                    style={{
                      paddingVertical: 10,
                      paddingHorizontal: 16,
                      borderWidth: 1.5,
                      borderColor: currentTheme?.theme?.primary,
                      backgroundColor: currentTheme?.theme?.transparent,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      maxWidth: quickQuestionsWidth + 35,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <AppText
                      numberOfLines={1}
                      style={{
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                        fontWeight: "bold",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: quickQuestionsWidth,
                        textAlign: "start",
                      }}
                      color={currentTheme?.theme?.primary}
                    >
                      {question}
                    </AppText>
                  </div>
                ) : (
                  <AppText
                    isClickable
                    key={index}
                    onPress={() => onSend(question)}
                    wrapperStyle={{
                      paddingVertical: 5,
                      paddingRight: 10,
                      paddingLeft: 5,
                      borderRadius: 5,
                      borderWidth: 1,
                      borderColor: currentTheme?.theme?.transparent,
                      backgroundColor: currentTheme?.theme?.primary_soft,
                    }}
                    style={{
                      marginLeft: 5,
                      fontSize: currentTheme?.theme?.extra_small_font_size,
                    }}
                    color={currentTheme?.theme?.primary}
                    leftIcon={AppAssets?.icons?.question_icon}
                    iconSize={16}
                  >
                    {question}
                  </AppText>
                ),
              )}
            </View>
          </div>
        )}
        {agentMode != "voice" && (
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleUnifiedFileUpload}
            multiple
            accept={acceptedFileTypes}
          />
        )}
      </div>

      {/* Debug Panel for LiveKit Voice Mode - Available in both dev and prod */}
      {showDebugPanel && isLiveKitMode && (
        <div
          style={{
            position: "fixed",
            top: "80px",
            right: isMobileScreen() ? "12px" : "20px",
            left: isMobileScreen() ? "12px" : undefined,
            width: isMobileScreen() ? "auto" : "320px",
            maxHeight: "450px",
            backgroundColor: currentTheme?.theme?.lightGrey,
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            zIndex: 9998,
            overflow: "hidden",
            fontFamily: "normalFont",
            fontSize: "11px",
            border: `1px solid ${currentTheme?.theme?.border_color}`,
            animation: "slideIn 0.2s ease-out",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "12px 14px",
              backgroundColor: currentTheme?.theme?.primary,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
            >
              <AppIcon
                icon={AppAssets?.icons?.terminal}
                size={14}
                color="#fff"
              />
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.small_font_size,
                  fontWeight: "600",
                }}
                color="#fff"
                preventTranslation
              >
                Live Mode Monitor
              </AppText>
            </View>
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: isLiveKitConnected
                    ? currentTheme?.theme?.green
                    : currentTheme?.theme?.red,
                }}
              />
              <AppText
                style={{ fontSize: currentTheme?.theme?.extra_small_font_size }}
                color="#fff"
                preventTranslation
              >
                {isLiveKitConnected
                  ? t("chats.livekit.status.connected")
                  : t("chats.livekit.status.disconnected")}
              </AppText>
              <div
                onClick={() => setShowDebugPanel(false)}
                style={{ cursor: "pointer", marginLeft: 4 }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.close}
                  size={14}
                  color="#fff"
                />
              </div>
            </View>
          </div>

          {/* Status Bar */}
          <div
            style={{
              padding: "10px 14px",
              backgroundColor: currentTheme?.theme?.primary_soft,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              borderBottom: `1px solid ${currentTheme?.theme?.border_color}`,
            }}
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <AppIcon
                icon={AppAssets?.icons?.network}
                size={12}
                color={currentTheme?.theme?.darkGrey}
              />
              <AppText
                style={{ fontSize: currentTheme?.theme?.extra_small_font_size }}
                color={currentTheme?.theme?.darkGrey}
                preventTranslation
              >
                Room:{" "}
                <strong style={{ color: currentTheme?.theme?.font_color }}>
                  {liveKitRoomName || "N/A"}
                </strong>
              </AppText>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <AppIcon
                icon={AppAssets?.icons?.agent}
                size={12}
                color={currentTheme?.theme?.darkGrey}
              />
              <AppText
                style={{ fontSize: currentTheme?.theme?.extra_small_font_size }}
                color={currentTheme?.theme?.darkGrey}
                preventTranslation
              >
                State:{" "}
                <strong
                  style={{
                    color:
                      liveKitAgentState === LIVEKIT_AGENT_STATES?.SPEAKING
                        ? currentTheme?.theme?.green
                        : liveKitAgentState === LIVEKIT_AGENT_STATES?.LISTENING
                        ? currentTheme?.theme?.primary
                        : liveKitAgentState === LIVEKIT_AGENT_STATES?.THINKING
                        ? currentTheme?.theme?.orange
                        : currentTheme?.theme?.darkGrey,
                  }}
                >
                  {liveKitAgentState || "N/A"}
                </strong>
              </AppText>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  marginLeft: 8,
                }}
                color={currentTheme?.theme?.darkGrey}
                preventTranslation
              >
                Mic:{" "}
                <strong
                  style={{
                    color: isLiveKitMicEnabled
                      ? currentTheme?.theme?.green
                      : currentTheme?.theme?.red,
                  }}
                >
                  {isLiveKitMicEnabled ? "ON" : "OFF"}
                </strong>
              </AppText>
            </View>

            {/* Audio Level Meter */}
            {isLiveKitMicEnabled && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 4,
                }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.liveMode}
                  size={12}
                  color={currentTheme?.theme?.darkGrey}
                />
                <div
                  style={{
                    flex: 1,
                    height: "8px",
                    backgroundColor: currentTheme?.theme?.mediumGrey,
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(liveKitAudioLevel * 100, 100)}%`,
                      height: "100%",
                      backgroundColor: isLiveKitAudioDetected
                        ? liveKitAudioLevel > 0.3
                          ? currentTheme?.theme?.green
                          : currentTheme?.theme?.orange
                        : currentTheme?.theme?.darkGrey,
                      borderRadius: "4px",
                      transition: "width 0.1s ease-out, background-color 0.1s",
                    }}
                  />
                </div>
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    minWidth: 35,
                    fontWeight: isLiveKitAudioDetected ? "bold" : "normal",
                  }}
                  color={
                    isLiveKitAudioDetected
                      ? currentTheme?.theme?.green
                      : currentTheme?.theme?.darkGrey
                  }
                  preventTranslation
                >
                  {(liveKitAudioLevel * 100).toFixed(0)}%
                </AppText>
              </View>
            )}

            {/* Active Audio Devices */}
            {(activeAudioDevices.input || activeAudioDevices.output) && (
              <View
                style={{
                  marginTop: 8,
                  padding: "8px 0",
                  borderTop: `1px solid ${currentTheme?.theme?.border_color}`,
                  gap: 4,
                }}
              >
                {activeAudioDevices.input && (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <AppText
                      style={{
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                      }}
                      color={currentTheme?.theme?.darkGrey}
                      preventTranslation
                    >
                      🎤
                    </AppText>
                    <AppText
                      style={{
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                        flex: 1,
                      }}
                      color={currentTheme?.theme?.font_color}
                      numberOfLines={1}
                      preventTranslation
                    >
                      {activeAudioDevices.input.label ||
                        t("chats.livekit.unknownMicrophone")}
                    </AppText>
                  </View>
                )}
                {activeAudioDevices.output && (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <AppText
                      style={{
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                      }}
                      color={currentTheme?.theme?.darkGrey}
                    >
                      🔊
                    </AppText>
                    <AppText
                      style={{
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                        flex: 1,
                      }}
                      color={currentTheme?.theme?.font_color}
                      numberOfLines={1}
                    >
                      {activeAudioDevices.output.label ||
                        t("chats.livekit.unknownSpeaker")}
                    </AppText>
                  </View>
                )}
              </View>
            )}

            {/* Transcripts */}
            {liveKitUserTranscript && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 6,
                  marginTop: 4,
                }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.human}
                  size={12}
                  color={currentTheme?.theme?.primary}
                />
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    flex: 1,
                  }}
                  color={currentTheme?.theme?.font_color}
                  numberOfLines={2}
                  preventTranslation
                >
                  {liveKitUserTranscript.slice(0, 80)}
                  {liveKitUserTranscript.length > 80 ? "..." : ""}
                </AppText>
              </View>
            )}
            {liveKitAgentTranscript && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 6,
                }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.agent}
                  size={12}
                  color={currentTheme?.theme?.green}
                />
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    flex: 1,
                  }}
                  color={currentTheme?.theme?.green}
                  numberOfLines={2}
                  preventTranslation
                >
                  {liveKitAgentTranscript.slice(0, 80)}
                  {liveKitAgentTranscript.length > 80 ? "..." : ""}
                </AppText>
              </View>
            )}
            {liveKitError && (
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.warning}
                  size={12}
                  color={currentTheme?.theme?.red}
                />
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                  }}
                  color={currentTheme?.theme?.red}
                  preventTranslation
                >
                  {liveKitError}
                </AppText>
              </View>
            )}
          </div>

          {/* Logs */}
          <div
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              padding: "8px 10px",
            }}
          >
            {liveKitLogs.length === 0 ? (
              <View style={{ alignItems: "center", padding: 20 }}>
                <AppIcon
                  icon={AppAssets?.icons?.messaging}
                  size={24}
                  color={currentTheme?.theme?.mediumGrey}
                />
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    marginTop: 8,
                  }}
                  color={currentTheme?.theme?.darkGrey}
                  preventTranslation
                >
                  No logs yet. Start speaking...
                </AppText>
              </View>
            ) : (
              liveKitLogs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    padding: log.type === "latency" ? "8px 10px" : "6px 8px",
                    marginBottom: "4px",
                    borderRadius: "6px",
                    backgroundColor:
                      log.type === "latency"
                        ? `${currentTheme?.theme?.primary}15`
                        : currentTheme?.theme?.inputBackgroundColor,
                    borderLeft: `3px solid ${
                      log.type === "error"
                        ? currentTheme?.theme?.red
                        : log.type === "latency"
                        ? currentTheme?.theme?.primary
                        : log.type === "connection"
                        ? currentTheme?.theme?.primary
                        : log.type === "agent"
                        ? currentTheme?.theme?.green
                        : log.type === "transcript"
                        ? currentTheme?.theme?.primary
                        : log.type === "system"
                        ? currentTheme?.theme?.orange
                        : currentTheme?.theme?.darkGrey
                    }`,
                    border:
                      log.type === "latency"
                        ? `1px solid ${currentTheme?.theme?.primary}40`
                        : "none",
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 2,
                    }}
                  >
                    <AppText
                      style={{ fontSize: "9px" }}
                      color={currentTheme?.theme?.darkGrey}
                      preventTranslation
                    >
                      {log.timestamp}
                    </AppText>
                    <span
                      style={{
                        fontSize: "9px",
                        padding: "1px 6px",
                        borderRadius: "4px",
                        backgroundColor:
                          log.type === "error"
                            ? `${currentTheme?.theme?.red}20`
                            : log.type === "latency"
                            ? currentTheme?.theme?.primary
                            : log.type === "connection"
                            ? `${currentTheme?.theme?.primary}20`
                            : log.type === "agent"
                            ? `${currentTheme?.theme?.green}20`
                            : log.type === "transcript"
                            ? `${currentTheme?.theme?.primary}20`
                            : log.type === "system"
                            ? `${currentTheme?.theme?.orange}20`
                            : currentTheme?.theme?.mediumGrey,
                        color:
                          log.type === "error"
                            ? currentTheme?.theme?.red
                            : log.type === "latency"
                            ? "#fff"
                            : log.type === "connection"
                            ? "#fff"
                            : log.type === "agent"
                            ? currentTheme?.theme?.green
                            : log.type === "transcript"
                            ? "#fff"
                            : log.type === "system"
                            ? currentTheme?.theme?.orange
                            : currentTheme?.theme?.darkGrey,
                        textTransform: "uppercase",
                        fontWeight: "600",
                        fontFamily: "normalFont",
                      }}
                    >
                      {log.type === "latency" ? "⚡ LATENCY" : log.type}
                    </span>
                  </View>
                  <AppText
                    style={{
                      fontSize:
                        log.type === "latency"
                          ? currentTheme?.theme?.small_font_size
                          : currentTheme?.theme?.extra_small_font_size,
                      fontWeight: log.type === "latency" ? "600" : "normal",
                    }}
                    color={
                      log.type === "latency"
                        ? currentTheme?.theme?.primary
                        : currentTheme?.theme?.font_color
                    }
                    preventTranslation
                  >
                    {log.message}
                  </AppText>
                </div>
              ))
            )}
          </div>

          {/* Actions */}
          <div
            style={{
              padding: "10px 14px",
              borderTop: `1px solid ${currentTheme?.theme?.border_color}`,
              display: "flex",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <div
              onClick={toggleLiveKitMicrophone}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: "6px",
                backgroundColor: isLiveKitMicEnabled
                  ? currentTheme?.theme?.red
                  : currentTheme?.theme?.green,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <AppIcon
                icon={
                  isLiveKitMicEnabled
                    ? AppAssets?.icons?.stopPlaying
                    : AppAssets?.icons?.microphone
                }
                size={12}
                color="#fff"
              />
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  fontWeight: "500",
                }}
                color="#fff"
                preventTranslation
              >
                {isLiveKitMicEnabled
                  ? t("chats.placeholders.mute")
                  : t("chats.placeholders.unmute")}
              </AppText>
            </div>
            <div
              onClick={clearLiveKitLogs}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: "6px",
                backgroundColor: "transparent",
                border: `1px solid ${currentTheme?.theme?.border_color}`,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <AppIcon
                icon={AppAssets?.icons?.clear}
                size={12}
                color={currentTheme?.theme?.darkGrey}
              />
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  fontWeight: "500",
                }}
                color={currentTheme?.theme?.darkGrey}
                preventTranslation
              >
                Clear Logs
              </AppText>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWebInput;
